import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';
import { downloadPDF } from '../components/PresentationPDF';
import ShippingEstimator from '../components/ShippingEstimator';
import ClientPicker from '../components/ClientPicker';
import SupplierPopover from '../components/SupplierPopover';
import { InventoryPanel } from '../components/InventoryDisclaimer';
import { nativeProductId } from '../lib/inventory';
import { assignCartLineId } from '../lib/cart';
import SearchPage from './SearchPage';
import { resolveColorHex } from '../lib/colorHex';
import { uploadFile } from '../lib/fileUpload';
import CreatePOFlow from './CreatePOFlow';
import CreateInvoiceModal from '../components/CreateInvoiceModal';

const G = BRAND.colors.primary;
// data_source → supplier slug for live per-size pricing (SanMar/S&S apparel).
const PRICING_SLUG = { direct_sanmar: 'sanmar', direct_ssactivewear: 'ssactivewear' };

// Canonical apparel size order (smallest → largest) for the size grid.
function sizeRank(label) {
  const s = String(label || '').trim().toUpperCase().replace(/\s+/g, '');
  const base = { 'XXXS':0,'XXS':1,'2XS':1,'XS':2,'S':3,'SM':3,'M':4,'MED':4,'L':5,'LG':5 };
  if (base[s] != null) return base[s];
  const numX = s.match(/^(\d+)X?L?$/);
  if (numX && /X/.test(s)) return 5 + parseInt(numX[1], 10);
  if (/^X+L$/.test(s)) return 5 + (s.length - 1);
  if (/^Y/.test(s)) return 100 + (sizeRank(s.slice(1)) || 0);
  return 900;
}

// Build size-grid rows from a {size: cost} map, preserving any quantities the
// user already entered (matched by size label). Used to re-price the grid when
// the selected color changes.
function sizesFromPriceMap(priceMap, existingSizes) {
  const qtyByLabel = {};
  (existingSizes || []).forEach(s => { if (s && s.label != null) qtyByLabel[s.label] = s.qty; });
  return Object.entries(priceMap || {}).map(([label, cost]) => ({
    label,
    cost: Number(cost),
    qty: qtyByLabel[label] != null ? qtyByLabel[label] : '',
  }));
}
const DC = { A:.5,B:.45,C:.4,D:.35,E:.3,F:.25,G:.2,P:.5,Q:.45,R:.4,S:.35,T:.3,U:.25,V:.2,X:0 };
const net  = (l,c) => +(l*(1-(DC[c]||0))).toFixed(2);
const sell = (n,m)  => +(n/(1-m/100)).toFixed(2);
const $$   = n       => '$'+Number(n).toFixed(2);
const EMOJIS = { Bags:'🎒',Drinkware:'🥤',Apparel:'👕',Tech:'⚡',Office:'📓' };

// Manual apparel garment size systems. Each is a default set of size labels for
// the per-size cost/qty grid; all are editable on the line (Custom lets you type
// your own). The API path (SanMar/S&S) will populate sizes from the supplier
// instead, but reuses the same grid + blended-cost math.
const SIZE_SYSTEMS = {
  adult:   { label: 'Adult',            sizes: ['XS','S','M','L','XL','2XL','3XL'] },
  youth:   { label: 'Youth',            sizes: ['YXS','YS','YM','YL','YXL'] },
  toddler: { label: 'Toddler / Infant', sizes: ['NB','6M','12M','18M','24M','2T','3T','4T','5T'] },
  osfm:    { label: 'One Size (OSFM)',  sizes: ['OSFM'] },
  custom:  { label: 'Custom',           sizes: [] },
};
const blankSizes = (systemKey) =>
  ((SIZE_SYSTEMS[systemKey] || {}).sizes || []).map(label => ({ label, cost: '', qty: '' }));

const hexCss = (h) => {
  if (!h) return null;
  return h.startsWith('#') ? h : `#${h}`;
};

const todayLabel = () => new Date().toLocaleDateString(undefined, { month:'short', day:'numeric' });

const LOGO_MAX_BYTES = 2 * 1024 * 1024;
const readFileAsDataURL = (file) => new Promise((resolve, reject) => {
  if (!file) return reject(new Error('No file'));
  if (!file.type.startsWith('image/')) return reject(new Error('Must be an image file'));
  if (file.size > LOGO_MAX_BYTES) return reject(new Error('Image too large (max 2MB)'));
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = () => reject(new Error('Failed to read file'));
  r.readAsDataURL(file);
});

// ─── Supplier charges (PPC data) — expandable per-product ────────────────────
// supplier_charges is the JSONB column we synced from PSRESTful's PPC service.
// Shape: [{ location, decoration, charges:[{name,type,description,price,xMinQty,xUom,yMinQty,yUom}], decorationSize, leadTime, isDefault }, ...]
function SupplierChargesPanel({ product:p, productQty, currentCharges, onAddCharge }) {
  const [open, setOpen] = useState(false);
  const groups = Array.isArray(p.supplier_charges) ? p.supplier_charges : [];
  if (groups.length === 0) return null;

  const isAlreadyAdded = (loc, dec, chargeName) =>
    (currentCharges||[]).some(c => c._source === `${loc}|${dec}|${chargeName}`);

  const handleAdd = (loc, dec, charge) => {
    const supplierCost = Number(charge.price) || 0;
    onAddCharge({
      _source: `${loc}|${dec}|${charge.name}`,
      name: `${charge.name} (${dec} on ${loc})`,
      type: charge.type, // 'Setup' or 'Run'
      supplier_cost: supplierCost,
      client_price: supplierCost, // default pass-through
      qty_for_run: charge.type === 'Run' ? productQty : null,
    });
  };

  return (
    <div style={{ marginTop:8 }}>
      <button onClick={()=>setOpen(o=>!o)}
        style={{ background:'none',border:'1px solid #E2E8F0',borderRadius:6,
          padding:'4px 11px',fontSize:11,color:'#64748B',cursor:'pointer',fontFamily:'inherit',
          display:'inline-flex',alignItems:'center',gap:5 }}>
        {open ? '▾' : '▸'} Supplier charges ({groups.reduce((n,g)=>n+(g.charges||[]).length,0)})
      </button>
      {open && (
        <div style={{ marginTop:8,background:'#FAFBFC',border:'1px solid #E2E8F0',borderRadius:7,padding:'10px 12px' }}>
          {groups.map((g, gi) => (
            <div key={gi} style={{ marginBottom: gi < groups.length-1 ? 12 : 0,paddingBottom: gi < groups.length-1 ? 12 : 0,
              borderBottom: gi < groups.length-1 ? '1px solid #F1F5F9' : 'none' }}>
              <div style={{ fontSize:11,fontWeight:600,color:'#374151',marginBottom:6 }}>
                {g.location} · {g.decoration}
                {g.decorationSize?.height && (
                  <span style={{ fontWeight:400,color:'#94A3B8',marginLeft:6 }}>
                    ({g.decorationSize.width}×{g.decorationSize.height} {g.decorationSize.uom})
                  </span>
                )}
              </div>
              {(g.charges||[]).map((c, ci) => {
                const added = isAlreadyAdded(g.location, g.decoration, c.name);
                return (
                  <div key={ci} style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'4px 0',gap:8 }}>
                    <div style={{ flex:1,minWidth:0 }}>
                      <div style={{ fontSize:11,color:'#0F172A' }}>
                        {c.name} <span style={{ color:'#94A3B8',fontWeight:400 }}>· {c.type}</span>
                      </div>
                      <div style={{ fontSize:10,color:'#94A3B8' }}>
                        Supplier cost: ${(Number(c.price)||0).toFixed(2)}{c.type==='Run' && c.xUom ? ` per ${c.xUom.toLowerCase()}` : ''}
                      </div>
                    </div>
                    <button onClick={()=>handleAdd(g.location, g.decoration, c)} disabled={added}
                      style={{ background: added ? '#E2E8F0' : G,border:'none',borderRadius:5,padding:'3px 10px',
                        color: added ? '#64748B' : '#fff',fontSize:10,fontWeight:500,
                        cursor: added ? 'default' : 'pointer',fontFamily:'inherit',flexShrink:0 }}>
                      {added ? 'Added' : '+ Add'}
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Decorator matrix charges (decorated-apparel quoting) ────────────────────
// Parallel to SupplierChargesPanel, but priced from the distributor's own
// decoration matrices (see the Decorators page) instead of a supplier's charge
// list. Pick a matrix + location + colors/stitches; it reads the matrix by the
// line's total quantity and appends charges: one Run charge for the per-piece
// decoration, plus any à la carte fees you tick (Setup, or Run for per-unit
// fees). A garment can be decorated in several locations — each "Add location"
// appends its own set, tagged with `location` so the decorator PO can break out
// per location while the client quote sums every location's per-piece charge.

const decoTypeShort = (t) => t === 'screen_print' ? 'Screen Print' : t === 'embroidery' ? 'Embroidery' : t === 'heat_transfer' ? 'Heat Transfer' : 'Decoration';

// ─── Manual (matrix-free) decoration jobs ────────────────────────────────────
// A distributor who hasn't built their decoration matrices yet still has to be
// able to quote decorated apparel — they already know their decorator charges
// $1.75 a shirt plus a $30 screen. A job whose matrixId is MANUAL_MATRIX_ID
// carries those numbers on the job itself (manualRunCost / manualSetupCost)
// instead of reading them out of a price grid.
//
// Everything downstream is deliberately identical to a matrix job: the run is
// still folded per-piece, setup is still charged once per distinct art, the
// decorator PO still lists garments grouped Art -> Ink. Only the SOURCE of the
// two numbers differs. These are COSTS, so sell() marks them up with the
// line's own margin exactly like matrix pricing — a manual job is not a
// client-price override.
//
// What a manual job deliberately does NOT get: a-la-carte fee checkboxes and
// the automatic color-change fee, both of which only exist inside a matrix.
const MANUAL_MATRIX_ID = '__manual__';
const isManualJob = (job) => !!job && (job.manual === true || job.matrixId === MANUAL_MATRIX_ID);
const manualDecoType = (job) => (job && job.manualType) || 'screen_print';
const manualRunCostOf = (job) => (job && job.manualRunCost !== '' && job.manualRunCost != null && isFinite(Number(job.manualRunCost)))
  ? Number(job.manualRunCost) : null;
const manualSetupCostOf = (job) => Number(job && job.manualSetupCost) || 0;

// Which qty tier a quantity falls into (highest break whose min <= qty).
function decoTierIndex(tiers, qty) {
  if (!Array.isArray(tiers) || !tiers.length || !(qty > 0)) return -1;
  let idx = -1;
  for (let i = 0; i < tiers.length; i++) {
    const min = Number(tiers[i].min);
    const max = (tiers[i].max == null || tiers[i].max === '') ? Infinity : Number(tiers[i].max);
    if (qty >= min && qty <= max) return i;
    if (qty >= min) idx = i;
  }
  return idx;
}

// Per-piece decoration cost from a matrix, given qty (for the tier) and the row
// key (color count for screen, stitch count for embroidery). null = unavailable.
function matrixUnitPrice(matrix, qty, rowKey) {
  const pricing = matrix.pricing || {};
  const ti = decoTierIndex(pricing.qty_tiers || [], qty);
  if (ti < 0) return null;
  const rows = pricing.rows || [];
  let row = null;
  if (matrix.type === 'screen_print') {
    row = rows.find(r => Number(r.colors) === Number(rowKey));
  } else if (matrix.type === 'embroidery' || matrix.type === 'heat_transfer') {
    row = rows.find(r => {
      const min = Number(r.min) || 0;
      const max = (r.max == null || r.max === '') ? Infinity : Number(r.max);
      return Number(rowKey) >= min && Number(rowKey) <= max;
    });
  } else {
    row = rows[0];
  }
  const v = row && row.prices ? row.prices[ti] : null;
  return (v == null || v === '') ? null : Number(v);
}

// Turn one à la carte fee into a charge for a location. per_unit → Run (× qty);
// per_color/per_screen → Setup × color count; flat/per_location → Setup flat.
function decoFeeToCharge(fee, colorCount, qty, margin, loc) {
  const amt = Number(fee.amount) || 0;
  const cc = Number(colorCount) || 1;
  let cost, type, run, note = '';
  if (fee.basis === 'per_unit') { cost = amt; type = 'Run'; run = qty; }
  else if (fee.basis === 'per_color' || fee.basis === 'per_screen') { cost = amt * cc; type = 'Setup'; run = null; note = ` (${cc} × $${amt.toFixed(2)})`; }
  else { cost = amt; type = 'Setup'; run = null; }
  return {
    _source: `deco|${loc}|${fee.name}`,
    name: `${fee.name}${note} · ${loc}`,   // internal (shows cost breakdown for us)
    client_label: `${fee.name} · ${loc}`,  // client-facing (no internal cost)
    type, supplier_cost: cost, client_price: sell(cost, margin),
    qty_for_run: run, location: loc, decoration: true,
  };
}

// ─── Combined-decoration job pricing ─────────────────────────────────────────
// A "job" is one imprint (matrix + location + colors/stitches) shared by several
// garments. The run charge prices at the COMBINED quantity of all member
// garments (so the whole job hits the right price break) and is folded into each
// garment's per-piece price; the setup/screen fee is charged ONCE at the order
// level. Returns run charges keyed by product id + order-level setup charges.
// Normalize members to [{id, art, ink}] (supports legacy memberIds arrays).
function jobMembers(job) {
  if (Array.isArray(job.members) && job.members.length) {
    return job.members.filter(m => m && m.id).map(m => ({
      id: m.id, art: (m.art || '').trim(), ink: (m.ink || '').trim(),
      artFileId: m.artFileId || null, artFileName: m.artFileName || null, artFileUrl: m.artFileUrl || null,
      artFileMime: m.artFileMime || null,
    }));
  }
  return (job.memberIds || []).filter(Boolean).map(id => ({ id, art: '', ink: '', artFileId: null, artFileName: null, artFileUrl: null, artFileMime: null }));
}

// The matrix's flat "Color Change" à-la-carte fee, if defined.
function colorChangeFee(matrix) {
  const fees = (matrix && matrix.pricing && matrix.pricing.charges) || [];
  return fees.find(f => /color\s*change/i.test(f.name || '')) || null;
}

function computeJobCharges(jobs, matrices, qtyOf, marginOf) {
  const runByProduct = {};
  const setups = [];
  // Per-member extra COGS from jobs flagged foldIntoGarment — e.g. a heat-
  // transfer material ordered from one vendor (priced like a decoration
  // matrix, sq-in × qty) that isn't really "decoration" at all so much as an
  // added ingredient cost on the garment itself. Folded jobs contribute here
  // instead of to runByProduct/setups: no separate charge line, no decorator
  // PO — the dollar amount is added straight into the garment's own net
  // cost, so your one existing margin formula applies to the combined total
  // and the client only ever sees one blended per-shirt price, the same way
  // a manual net override already works today.
  const foldedNetByProduct = {};
  for (const job of jobs || []) {
    // A manual job has no matrix at all — its rate and setup live on the job.
    // Everything below branches on `manual` only where the NUMBER comes from;
    // the folding, setup-per-art and PO-shaping rules are untouched.
    const manual = isManualJob(job);
    const matrix = manual ? null : (matrices || []).find(m => m.id === job.matrixId);
    if (!matrix && !manual) continue;
    const decoType = manual ? manualDecoType(job) : matrix.type;
    const members = jobMembers(job);
    if (!members.length) continue;
    // The row-selector value fed into matrixUnitPrice: ink colors (screen
    // print), stitch count (embroidery, via job.count same as screen print's
    // field), or computed square inches (heat transfer, from width × height
    // — the distributor enters dimensions, not the multiplied total).
    const count = decoType === 'heat_transfer'
      ? (Number(job.printWidth) || 0) * (Number(job.printHeight) || 0)
      : Number(job.count) || (decoType === 'screen_print' ? 1 : 0);
    const combinedQty = members.reduce((s, m) => s + (Number(qtyOf(m.id)) || 0), 0);
    if (combinedQty <= 0) continue;
    // Same artwork on multiple locations → each location is an extra print per
    // piece (like an additional garment). Run prices at total impressions
    // (pieces × locations); setup stays at one screen (same art).
    const locList = (Array.isArray(job.locations) && job.locations.length ? job.locations : [job.location])
      .map(l => (l || '').trim()).filter(Boolean);
    const locCount = Math.max(1, locList.length);
    const locLabel = locList.join(' + ') || 'Location';
    const impressions = combinedQty * locCount;
    // matrixUnitPrice returns the raw cell value. For screen print/embroidery
    // that cell already IS the final per-piece rate. For heat transfer the
    // matrix is priced PER SQUARE INCH (matches the header "Per-piece price
    // by print size (sq in) × quantity" and the source pricing sheets this
    // was modeled on) — the cell is $/sq-in, so the real per-piece rate is
    // cell × sq-in, not the cell alone. `count` already holds the computed
    // sq-in for heat transfer (see above), so multiply it in here, once,
    // right at the source — everything downstream (run cost, PO, PDF) reads
    // from this corrected value.
    const rawRate = manual ? null : matrixUnitPrice(matrix, impressions, count);   // rate at the impression break
    const matrixRate = (rawRate != null && decoType === 'heat_transfer') ? rawRate * count : rawRate;
    // Manual jobs: the typed cost IS the per-print rate. Like a matrix rate it
    // is multiplied by the location count below, so $1.75 on a two-location
    // job costs $3.50 a piece — each location is another print either way.
    const runRate = manual ? manualRunCostOf(job) : matrixRate;
    const setupMargin = marginOf(members[0].id);

    // Run — folded per member; each location adds a print to the per-piece cost.
    // job.runCostOverride (when set) replaces the matrix-computed rate entirely
    // — for cases like heat transfers where the real cost (transfer + decorator
    // application fee) doesn't match what the matrix grid computes. Margin
    // still applies automatically on top via sell(), same as always; this is a
    // COST override, not a client-price override, so it reflows correctly
    // if margin changes later instead of needing the client price re-typed.
    const hasOverride = job.runCostOverride != null && job.runCostOverride !== '';
    const effectiveRunCost = hasOverride ? Number(job.runCostOverride) : (runRate != null ? runRate * locCount : null);
    if (effectiveRunCost != null) {
      if (job.foldIntoGarment) {
        for (const m of members) {
          const q = Number(qtyOf(m.id)) || 0;
          if (q <= 0) continue;
          foldedNetByProduct[m.id] = (foldedNetByProduct[m.id] || 0) + effectiveRunCost;
        }
      } else {
        for (const m of members) {
          const q = Number(qtyOf(m.id)) || 0;
          if (q <= 0) continue;
          (runByProduct[m.id] = runByProduct[m.id] || []).push({
            _source: `job|${job.id}|run`,
            name: `${decoTypeShort(decoType)} · ${locLabel} · run ${impressions}`,
            type: 'Run', supplier_cost: effectiveRunCost, client_price: sell(effectiveRunCost, marginOf(m.id)),
            qty_for_run: q, location: locLabel, decoration: true,
            matrix_id: matrix ? matrix.id : null, decoration_type: decoType, job_id: job.id,
            job_manual: manual || undefined,
            job_sides: locCount, job_impressions: impressions, job_run_rate: runRate,
            cost_overridden: hasOverride,
          });
        }
      }
    }

    // Distinct arts (first-seen order) → one set of screen setups each.
    const arts = [];
    for (const m of members) { const a = m.art || 'Art'; if (!arts.includes(a)) arts.push(a); }
    const ccFee = manual ? null : colorChangeFee(matrix);
    // A manual job has no a-la-carte fee list, so its single typed setup cost
    // stands in for the whole fee list. It follows the SAME rule as a matrix
    // setup: charged once per distinct art, never per item and never per
    // location. One logo on three garments across two locations = one setup.
    const selFees = manual
      ? (manualSetupCostOf(job) > 0 ? [{ name: 'Setup', amount: manualSetupCostOf(job), basis: 'flat' }] : [])
      : (job.feeIndexes || []).map(i => ((matrix.pricing || {}).charges || [])[i]).filter(f => f && f !== ccFee);
    for (const art of arts) {
      for (const f of selFees) {
        const ch = decoFeeToCharge(f, count, combinedQty, setupMargin, `${locLabel} · ${art}`);
        if (ch.type !== 'Setup') continue;
        if (job.foldIntoGarment) {
          // One-time setup folded as a per-unit addition, spread across this
          // job's combined qty, same principle as the run cost above — no
          // separate line, just more COGS on the garment.
          const perUnit = (Number(ch.supplier_cost) || 0) / combinedQty;
          for (const m of members) {
            const q = Number(qtyOf(m.id)) || 0;
            if (q <= 0) continue;
            foldedNetByProduct[m.id] = (foldedNetByProduct[m.id] || 0) + perUnit;
          }
        } else {
          setups.push({ ...ch, _source: `job|${job.id}|setup|${art}`, job_id: job.id });
        }
      }
    }

    // Color changes — one flat fee per ADDITIONAL distinct ink under an art.
    //
    // BUG FIX: this used to be `if (ccFee && !job.foldIntoGarment)`, so folding
    // a job DROPPED the color-change fee entirely — it wasn't charged to the
    // client and wasn't added to COGS either, so the distributor silently ate
    // it. Folding is supposed to change WHERE a cost appears, never whether it
    // exists. Now it follows the same rule as setups above: folded jobs spread
    // it per-unit into the garment's cost, unfolded jobs get a separate line.
    if (ccFee) {
      for (const art of arts) {
        const inks = [];
        for (const m of members) if ((m.art || 'Art') === art && m.ink && !inks.includes(m.ink)) inks.push(m.ink);
        for (let k = 0; k < Math.max(0, inks.length - 1); k++) {
          const ch = decoFeeToCharge(ccFee, 1, combinedQty, setupMargin, `${locLabel} · ${art}`);
          if (job.foldIntoGarment) {
            const perUnit = (Number(ch.supplier_cost) || 0) / combinedQty;
            for (const m of members) {
              const q = Number(qtyOf(m.id)) || 0;
              if (q <= 0) continue;
              foldedNetByProduct[m.id] = (foldedNetByProduct[m.id] || 0) + perUnit;
            }
          } else {
            setups.push({
              ...ch, _source: `job|${job.id}|cc|${art}|${k}`, job_id: job.id,
              name: `Color change · ${art} · ${locLabel}`,
              client_label: `Color change · ${locLabel}`,
            });
          }
        }
      }
    }
  }
  return { runByProduct, setups, foldedNetByProduct };
}

// ─── Per-product charges list (added by distributor, editable client price) ──
function ProductChargesList({ charges, onChangeClientPrice, onChangeRunQty, onOverrideRunCost, onRemove }) {
  if (!charges || charges.length === 0) return null;
  return (
    <div style={{ marginTop:10,background:'#fff',border:`1px solid ${G}33`,borderRadius:7,padding:'8px 10px' }}>
      <div style={{ fontSize:10,color:G,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:6 }}>
        On this product's quote ({charges.length})
      </div>
      {charges.map((c, i) => (
        <div key={i} style={{ display:'flex',alignItems:'center',gap:8,padding:'4px 0',
          borderTop: i > 0 ? '1px solid #F8FAFC' : 'none' }}>
          <div style={{ flex:1,minWidth:0 }}>
            <div style={{ fontSize:11,color:'#0F172A',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>
              {c.name}
            </div>
            <div style={{ fontSize:10,color:'#94A3B8',display:'flex',alignItems:'center',gap:2 }}>
              {c.type}
              {c.type==='Run' && c.job_id && onOverrideRunCost ? (
                <span style={{ display:'inline-flex',alignItems:'center',gap:2 }}>
                  · cost $<input type="number" step="0.01" min="0"
                    value={c.supplier_cost ?? ''}
                    onChange={e=>onOverrideRunCost(c.job_id, e.target.value === '' ? null : parseFloat(e.target.value))}
                    title={c.cost_overridden ? 'Overridden — click the × to remove and revert to matrix pricing' : 'Matrix-computed. Type a value to override with your real cost.'}
                    style={{ width:50,height:18,border:c.cost_overridden?`1px solid ${G}`:'1px solid #E2E8F0',borderRadius:4,padding:'0 4px',
                      fontSize:10,fontFamily:'inherit',outline:'none',color:c.cost_overridden?G:'#0F172A',background:'#fff' }}/>
                  {c.cost_overridden && (
                    <button onClick={()=>onOverrideRunCost(c.job_id, null)} title="Revert to matrix pricing"
                      style={{ background:'none',border:'none',cursor:'pointer',color:'#94A3B8',fontSize:11,padding:0,fontFamily:'inherit' }}>↺</button>
                  )}
                </span>
              ) : (
                <span>&nbsp;· supplier ${(Number(c.supplier_cost)||0).toFixed(2)}</span>
              )}
              {c.type==='Run' && (
                <span> · qty: <input type="number" min="1"
                  value={c.qty_for_run||''}
                  onChange={e=>onChangeRunQty(i, e.target.value ? parseInt(e.target.value,10) : null)}
                  style={{ width:50,height:18,border:'1px solid #E2E8F0',borderRadius:4,padding:'0 5px',
                    fontSize:10,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',marginLeft:2 }}/></span>
              )}
            </div>
          </div>
          <div style={{ display:'flex',alignItems:'center',gap:4,flexShrink:0 }}>
            <span style={{ fontSize:10,color:'#94A3B8' }}>Client $</span>
            <input type="number" step="0.01" min="0"
              value={c.client_price ?? ''}
              onChange={e=>onChangeClientPrice(i, e.target.value === '' ? 0 : parseFloat(e.target.value))}
              style={{ width:64,height:22,border:'1px solid #E2E8F0',borderRadius:5,padding:'0 6px',
                fontSize:11,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',textAlign:'right' }}/>
          </div>
          <button onClick={()=>onRemove(i)}
            style={{ background:'none',border:'none',cursor:'pointer',color:'#94A3B8',fontSize:14,padding:'0 4px',fontFamily:'inherit' }}>×</button>
        </div>
      ))}
    </div>
  );
}

// ─── Mockup upload button (per row) ──────────────────────────────────────────
// ─── Price grid preview (distributor view; lets per-tier margin and price be edited) ──
// Shows what the client will see in the PDF / public viewer when "Show full price grid"
// is enabled. Each tier has an editable margin AND an editable client price.
// Distributor's row-level margin propagates to all tiers unless overridden per tier.
function PriceGridPreview({ product:p, defaultMargin, currentQty, tierOverrides,
                            onSetTierMargin, onSetTierPriceOverride, onResetAll }) {
  const tiers = (p.pricing || []).map(t => ({ ...t, qty: t.qty || t.q, list: t.list || t.l, code: t.code || t.c }));
  if (tiers.length === 0) return null;

  // Determine which tier the current row qty falls into (highest break <= currentQty).
  let activeIdx = 0;
  for (let i = 0; i < tiers.length; i++) {
    if (currentQty >= tiers[i].qty) activeIdx = i;
    else break;
  }

  // Helper: net price for a tier
  const tierNet = (t) => +(((t.list || 0) * (1 - (DC[t.code] || 0))).toFixed(2));

  // Helper: per-tier override values
  const ovFor = (qty) => tierOverrides?.[qty] || {};

  const hasAnyOverride = tiers.some(t => {
    const ov = ovFor(t.qty);
    return (typeof ov.margin === 'number') || (typeof ov.price_override === 'number');
  });

  return (
    <div style={{ marginTop:12,background:'#F8FAFC',border:'1px solid #E2E8F0',borderRadius:8,padding:'12px 14px' }}>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8 }}>
        <div style={{ fontSize:11,color:'#475569',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px' }}>
          Volume pricing preview
        </div>
        {hasAnyOverride && (
          <button onClick={onResetAll}
            style={{ background:'none',border:'none',color:G,fontSize:11,cursor:'pointer',
              fontFamily:'inherit',padding:0,textDecoration:'underline' }}>
            ↺ Reset all tiers to default
          </button>
        )}
      </div>
      <table style={{ width:'100%',borderCollapse:'collapse',fontSize:11 }}>
        <thead>
          <tr style={{ borderBottom:'1px solid #E2E8F0' }}>
            <th style={{ textAlign:'left',padding:'4px 6px',color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',fontSize:10 }}>Qty</th>
            <th style={{ textAlign:'right',padding:'4px 6px',color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',fontSize:10 }}>List</th>
            <th style={{ textAlign:'right',padding:'4px 6px',color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',fontSize:10 }}>Net</th>
            <th style={{ textAlign:'center',padding:'4px 6px',color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',fontSize:10 }}>Margin</th>
            <th style={{ textAlign:'right',padding:'4px 6px',color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',fontSize:10 }}>Client price</th>
          </tr>
        </thead>
        <tbody>
          {tiers.map((t, i) => {
            const isActive = i === activeIdx;
            const ov = ovFor(t.qty);
            const tNet = tierNet(t);
            const tierMargin = typeof ov.margin === 'number' ? ov.margin : defaultMargin;
            // Client price: explicit override wins over computed sell-from-margin.
            const computed = +(tNet / (1 - tierMargin / 100)).toFixed(2);
            const clientPrice = typeof ov.price_override === 'number' ? ov.price_override : computed;
            const marginOverridden = typeof ov.margin === 'number';
            const priceOverridden = typeof ov.price_override === 'number';
            return (
              <tr key={t.qty}
                style={{
                  background: isActive ? `${G}10` : 'transparent',
                  borderBottom: i < tiers.length - 1 ? '1px solid #F1F5F9' : 'none',
                }}>
                <td style={{ padding:'5px 6px',fontWeight:isActive?600:400,color:isActive?'#059669':'#0F172A' }}>
                  {isActive && <span style={{ marginRight:4 }}>👉</span>}
                  {t.qty}+
                </td>
                <td style={{ padding:'5px 6px',textAlign:'right',color:'#94A3B8',textDecoration:'line-through' }}>
                  ${(t.list || 0).toFixed(2)}
                </td>
                <td style={{ padding:'5px 6px',textAlign:'right',color:'#475569' }}>
                  ${tNet.toFixed(2)}
                </td>
                <td style={{ padding:'5px 6px',textAlign:'center' }}>
                  <input type="number" min="1" max="80"
                    value={tierMargin}
                    onChange={e=>{
                      const v = parseInt(e.target.value, 10);
                      if (!isNaN(v)) onSetTierMargin(t.qty, v);
                    }}
                    style={{ width:46,height:22,border:`1px solid ${marginOverridden?G:'#E2E8F0'}`,borderRadius:4,
                      padding:'0 4px',fontSize:11,fontFamily:'inherit',outline:'none',
                      color: marginOverridden ? '#059669' : '#0F172A',
                      fontWeight: marginOverridden ? 600 : 400,
                      background:'#fff',textAlign:'center' }}/>
                  <span style={{ fontSize:10,color:'#94A3B8',marginLeft:2 }}>%</span>
                </td>
                <td style={{ padding:'5px 6px',textAlign:'right' }}>
                  <span style={{ fontSize:10,color:'#94A3B8',marginRight:2 }}>$</span>
                  <input type="number" step="0.01" min="0"
                    value={clientPrice.toFixed(2)}
                    onChange={e=>{
                      const v = parseFloat(e.target.value);
                      if (!isNaN(v) && v >= 0) {
                        // If the new value matches what margin would produce, clear the override.
                        const matchesMargin = Math.abs(v - computed) < 0.005;
                        onSetTierPriceOverride(t.qty, matchesMargin ? null : +v.toFixed(2));
                      }
                    }}
                    style={{ width:64,height:22,border:`1px solid ${priceOverridden?G:'#E2E8F0'}`,borderRadius:4,
                      padding:'0 5px',fontSize:11,fontFamily:'inherit',outline:'none',
                      color: priceOverridden ? '#059669' : '#0F172A',
                      fontWeight: priceOverridden ? 600 : 500,
                      background:'#fff',textAlign:'right' }}/>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ fontSize:10,color:'#94A3B8',marginTop:8,lineHeight:1.5 }}>
        Edit margin or client price per tier. Default margin from row above propagates unless overridden.
      </div>
    </div>
  );
}

function MockupButton({ mockup, onUpload, onRemove }) {
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    setError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await readFileAsDataURL(file);
      onUpload(dataUrl);
    } catch (err) {
      setError(err.message);
    }
    e.target.value = '';
  };

  if (mockup) {
    return (
      <div style={{ display:'flex',alignItems:'center',gap:8,marginTop:8 }}>
        <img src={mockup} alt="Mockup"
          style={{ width:32,height:32,borderRadius:5,objectFit:'contain',border:`1px solid ${G}`,background:'#fff',padding:2 }}/>
        <span style={{ fontSize:11,color:G,fontWeight:500 }}>Mockup uploaded</span>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display:'none' }}/>
        <button onClick={()=>fileRef.current?.click()}
          style={{ background:'none',border:'none',color:'#64748B',fontSize:11,cursor:'pointer',fontFamily:'inherit',padding:'0 4px',textDecoration:'underline' }}>
          Replace
        </button>
        <button onClick={onRemove}
          style={{ background:'none',border:'none',color:'#94A3B8',fontSize:11,cursor:'pointer',fontFamily:'inherit',padding:'0 4px',textDecoration:'underline' }}>
          Remove
        </button>
      </div>
    );
  }

  return (
    <>
      <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display:'none' }}/>
      <button onClick={()=>fileRef.current?.click()}
        style={{ marginTop:8,background:'none',border:'1px solid #E2E8F0',borderRadius:6,
          padding:'3px 10px',fontSize:11,color:'#64748B',cursor:'pointer',fontFamily:'inherit',
          display:'inline-flex',alignItems:'center',gap:4 }}>
        🎨 Upload mockup
      </button>
      {error && <div style={{ fontSize:11,color:'#EF4444',marginTop:4 }}>{error}</div>}
    </>
  );
}

// Generic per-line-item art attachment — same upload/dedup/library-pick
// mechanics as DecorationJobsPanel's per-member art, but scoped to a single
// cart line rather than a decoration-job member. This is what closes the gap
// for non-apparel items, which never go through a decoration job at all.
function ItemArtButton({ art, onSetArt, clientId, getToken, showToast }) {
  const [uploading, setUploading] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryFiles, setLibraryFiles] = useState(null);
  const [libraryLoading, setLibraryLoading] = useState(false);

  const attach = async (fileList, replacesFileId) => {
    const file = fileList && fileList[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = await getToken();
      const uploaded = await uploadFile(file, { kind: 'art', clientId, label: file.name, replacesFileId }, token);
      onSetArt({ fileId: uploaded.id, fileName: uploaded.filename || file.name, fileUrl: uploaded.url || null, fileMime: uploaded.mime_type || file.type || null });
      showToast?.(uploaded.reused ? 'Art already on file — reused existing upload' : 'Art attached');
    } catch (e) {
      showToast?.('Art upload failed: ' + e.message, 'error');
    } finally {
      setUploading(false);
    }
  };
  const remove = () => onSetArt(null);
  const openLibrary = async () => {
    setLibraryOpen(true);
    if (libraryFiles) return;
    setLibraryLoading(true);
    try {
      const token = await getToken();
      const { files } = await api.withToken(token).getClientFiles(clientId, 'art');
      setLibraryFiles(files);
    } catch (e) {
      showToast?.('Could not load art library: ' + e.message, 'error');
      setLibraryFiles([]);
    } finally {
      setLibraryLoading(false);
    }
  };
  const pick = (file) => {
    onSetArt({ fileId: file.id, fileName: file.filename || file.label, fileUrl: file.url || null, fileMime: file.mime_type || null });
    setLibraryOpen(false);
  };

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {art?.fileId ? (
          <>
            {art.fileMime && art.fileMime.startsWith('image/') ? (
              <a href={art.fileUrl} target="_blank" rel="noreferrer">
                <img src={art.fileUrl} alt={art.fileName || 'Art'}
                  style={{ width: 32, height: 32, borderRadius: 5, objectFit: 'contain', border: '1px solid #E2E8F0', background: '#fff', padding: 2 }} />
              </a>
            ) : null}
            <a href={art.fileUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: G, textDecoration: 'none' }}>
              📎 {art.fileName || 'Attached art'}
            </a>
            <label style={{ fontSize: 11, color: '#64748B', cursor: 'pointer', textDecoration: 'underline' }}>
              Replace
              <input type="file" accept="image/*,.pdf,.ai,.eps,.svg" style={{ display: 'none' }}
                onChange={e => attach(e.target.files, art.fileId)} />
            </label>
            <button onClick={remove} style={{ background: 'none', border: 'none', color: '#B91C1C', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
              Remove
            </button>
          </>
        ) : (
          <>
            <label style={{ fontSize: 12, color: G, cursor: 'pointer', fontWeight: 500 }}>
              {uploading ? 'Uploading…' : '📎 Attach art'}
              <input type="file" accept="image/*,.pdf,.ai,.eps,.svg" style={{ display: 'none' }} disabled={uploading}
                onChange={e => attach(e.target.files, null)} />
            </label>
            {clientId && (
              <button onClick={openLibrary} style={{ background: 'none', border: 'none', color: '#475569', fontSize: 11, textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
                Use existing art
              </button>
            )}
          </>
        )}
      </div>
      {libraryOpen && (
        <div style={{ marginTop: 6, border: '1px solid #E2E8F0', borderRadius: 6, padding: 8, background: '#fff', maxHeight: 160, overflowY: 'auto' }}>
          {libraryLoading ? (
            <div style={{ fontSize: 11, color: '#94A3B8' }}>Loading this client's art…</div>
          ) : !libraryFiles || libraryFiles.length === 0 ? (
            <div style={{ fontSize: 11, color: '#94A3B8' }}>No previous art on file for this client yet.</div>
          ) : (
            libraryFiles.map(f => (
              <div key={f.id} onClick={() => pick(f)}
                style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 6px', fontSize: 11, color: '#334155', cursor: 'pointer', borderRadius: 4 }}
                onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span>📎 {f.filename || f.label || 'Untitled'}</span>
                <span style={{ color: '#94A3B8' }}>{new Date(f.created_at).toLocaleDateString()}</span>
              </div>
            ))
          )}
          <button onClick={() => setLibraryOpen(false)} style={{ marginTop: 4, background: 'none', border: 'none', color: '#94A3B8', fontSize: 10, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
            Close
          </button>
        </div>
      )}
    </div>
  );
}

function AdditionalChargesSection({ charges, onAdd, onChange, onRemove }) {
  return (
    <div style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:10,padding:14,marginBottom:10 }}>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:charges.length?10:0 }}>
        <div style={{ fontSize:13,fontWeight:600,color:'#0F172A' }}>Additional charges</div>
        <button onClick={onAdd}
          style={{ background:'none',border:`1px solid ${G}`,borderRadius:6,padding:'4px 11px',
            fontSize:11,color:G,fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>
          + Add charge
        </button>
      </div>
      {charges.length === 0 ? (
        <div style={{ fontSize:11,color:'#94A3B8',marginTop:8 }}>
          Shipping, rush charges, anything that's not tied to a specific product.
        </div>
      ) : (
        charges.map((c, i) => {
          const isJob = String(c._source || '').startsWith('job|');
          if (isJob) {
            return (
              <div key={i} style={{ display:'flex',alignItems:'center',gap:8,padding:'6px 0',
                borderTop: i > 0 ? '1px solid #F8FAFC' : 'none' }}>
                <span style={{ flex:1,fontSize:12,color:'#475569' }}>{c.name||'Decoration setup'}
                  <span style={{ fontSize:10,color:'#94A3B8',marginLeft:6 }}>· from decoration job</span>
                </span>
                <span style={{ fontSize:12,color:'#475569',fontWeight:600 }}>${(Number(c.client_price)||0).toFixed(2)}</span>
                <span style={{ width:20 }}/>
              </div>
            );
          }
          return (
          <div key={i} style={{ display:'flex',alignItems:'center',gap:8,padding:'6px 0',
            borderTop: i > 0 ? '1px solid #F8FAFC' : 'none' }}>
            <input value={c.name||''} onChange={e=>onChange(i,'name',e.target.value)} placeholder="e.g., Shipping"
              style={{ flex:1,height:28,border:'1px solid #E2E8F0',borderRadius:5,padding:'0 9px',
                fontSize:12,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff' }}/>
            <span style={{ fontSize:11,color:'#94A3B8' }}>$</span>
            <input type="number" step="0.01" min="0" value={c.client_price ?? ''}
              onChange={e=>onChange(i,'client_price', e.target.value === '' ? 0 : parseFloat(e.target.value))}
              placeholder="0.00"
              style={{ width:84,height:28,border:'1px solid #E2E8F0',borderRadius:5,padding:'0 9px',
                fontSize:12,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',textAlign:'right' }}/>
            <button onClick={()=>onRemove(i)}
              style={{ background:'none',border:'none',cursor:'pointer',color:'#94A3B8',fontSize:16,padding:'0 4px',fontFamily:'inherit' }}>×</button>
          </div>
          );
        })
      )}
    </div>
  );
}

// ─── Client color picker for the distributor (override the requested color) ──
// The customer picks a color in the storefront. The distributor needs to be
// able to change it (clients change their minds). Click the pill → dropdown.
function ClientColorPicker({ value, originalValue, availableColors, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!value && (!availableColors || availableColors.length === 0)) return null;

  const colors = availableColors || [];
  const isOverridden = originalValue && value && value.name !== originalValue.name;

  return (
    <div ref={ref} style={{ position:'relative',marginBottom:8,display:'inline-block' }}>
      <button onClick={()=>setOpen(o=>!o)}
        style={{ display:'inline-flex',alignItems:'center',gap:6,
          background: isOverridden ? '#FEF3C7' : `${G}10`,
          border: `1px solid ${isOverridden ? '#FCD34D' : G+'33'}`,
          borderRadius:6,padding:'3px 9px',fontSize:11,
          color: isOverridden ? '#92400E' : '#059669',
          cursor:'pointer',fontFamily:'inherit' }}>
        {value && (
          <span style={{ width:10,height:10,borderRadius:'50%',
            background:hexCss(resolveColorHex(value))||'#ccc',border:'1px solid #E2E8F0' }}/>
        )}
        <span>
          {value ? <>Color: <strong>{value.name}</strong></> : 'Pick color'}
          {isOverridden && <span style={{ fontWeight:400,marginLeft:4 }}>(was {originalValue.name})</span>}
        </span>
        <span style={{ fontSize:9,opacity:0.7 }}>▾</span>
      </button>
      {open && (
        <div style={{ position:'absolute',top:'calc(100% + 4px)',left:0,zIndex:50,
          background:'#fff',border:'1px solid #E2E8F0',borderRadius:8,
          boxShadow:'0 8px 24px rgba(15,23,42,0.12)',
          minWidth:180,maxHeight:240,overflowY:'auto',padding:'4px 0' }}>
          {isOverridden && (
            <div onClick={()=>{onChange(originalValue);setOpen(false);}}
              style={{ padding:'7px 12px',fontSize:11,cursor:'pointer',color:'#92400E',
                display:'flex',alignItems:'center',gap:8,borderBottom:'1px solid #F1F5F9' }}
              onMouseEnter={e=>e.currentTarget.style.background='#FEF3C7'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              ↺ Reset to client's original ({originalValue.name})
            </div>
          )}
          {colors.map((c,i) => (
            <div key={i} onClick={()=>{onChange({name:c.name,hex:resolveColorHex(c)});setOpen(false);}}
              style={{ padding:'7px 12px',fontSize:12,cursor:'pointer',color:'#0F172A',
                display:'flex',alignItems:'center',gap:8,
                background: value?.name===c.name ? `${G}10` : 'transparent' }}
              onMouseEnter={e=>{ if (value?.name!==c.name) e.currentTarget.style.background='#F8FAFC'; }}
              onMouseLeave={e=>{ if (value?.name!==c.name) e.currentTarget.style.background='transparent'; }}>
              <span style={{ width:14,height:14,borderRadius:'50%',
                background:hexCss(resolveColorHex(c))||'#ccc',border:'1px solid #E2E8F0',flexShrink:0 }}/>
              <span style={{ overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{c.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Inline product search + custom item toolbar ─────────────────────────────
function AddProductBar({ onOpenSearch, onAddCustomClick, onAddApparelClick }) {
  return (
    <div style={{ display:'flex',gap:8,marginBottom:14 }}>
      <button onClick={onOpenSearch}
        style={{ flex:1,display:'flex',alignItems:'center',gap:8,background:'#fff',border:'1px solid #E2E8F0',
          borderRadius:9,padding:'10px 14px',fontSize:13,color:'#64748B',cursor:'pointer',fontFamily:'inherit',textAlign:'left' }}>
        <span style={{ fontSize:14 }}>🔍</span>
        Search to add a product to this presentation…
      </button>
      {/* Manual apparel entry — a garment with a per-size grid (qty + cost per
          size) and no live supplier API behind it. Always shown, connected or
          not: it covers the wait for SanMar/S&S approval AND any blank neither
          of them carries (e.g. A4 bought direct). Downstream it's a normal
          apparel line — decoration jobs, margin, and its own apparel PO. */}
      <button onClick={onAddApparelClick}
        style={{ background:'#fff',border:`1px solid ${G}`,borderRadius:9,padding:'10px 16px',
          color:G,fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap' }}>
        + Add apparel garment
      </button>
      <button onClick={onAddCustomClick}
        style={{ background:'#fff',border:`1px solid ${G}`,borderRadius:9,padding:'10px 16px',
          color:G,fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap' }}>
        + Add custom item
      </button>
    </div>
  );
}

// ─── Product search modal ─────────────────────────────────────────────────────
// Replaces the old inline-dropdown search (text-only rows, no images, no
// filters) with the real Search Products experience — images, category/
// supplier facets, AI re-rank — in an overlay so you never leave the
// presentation you're building. SearchPage is already fully self-contained
// (its own state, fetching, filters) and only needs these four props, so
// this wrapper is just presentation — no search logic duplicated here.
function ProductSearchModal({ onClose, getToken, addToCart, cartItems, showToast }) {
  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(15,23,42,0.55)',zIndex:300,
      display:'flex',alignItems:'flex-start',justifyContent:'center',backdropFilter:'blur(4px)',
      padding:'3vh 20px',overflowY:'auto' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'#F8FAFC',borderRadius:14,width:'100%',maxWidth:1180,
        maxHeight:'94vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.25)',position:'relative' }}>
        <div style={{ position:'sticky',top:0,zIndex:1,display:'flex',justifyContent:'space-between',alignItems:'center',
          padding:'14px 20px',background:'#fff',borderBottom:'1px solid #E2E8F0' }}>
          <div style={{ fontSize:15,fontWeight:600,color:'#0F172A' }}>Add a product to this presentation</div>
          <button onClick={onClose}
            style={{ background:'none',border:'none',color:'#94A3B8',fontSize:22,cursor:'pointer',padding:0,lineHeight:1 }}>×</button>
        </div>
        <div style={{ padding:20 }}>
          <SearchPage getToken={getToken} addToCart={addToCart} cartItems={cartItems} showToast={showToast} />
        </div>
      </div>
    </div>
  );
}

// ─── Custom item form (off-system products) ──────────────────────────────────
// ─── Apparel size grid (per-line: cost + qty per size) ───────────────────────
// For manual (non-API) garments. Each size has its own per-piece cost and qty —
// S–XL might share a price, 2XL another, 3XL another. Total qty drives the
// decoration tier + pricing; the blended (qty-weighted) cost becomes the line's
// net, so the existing totals/PDF math works unchanged.
const apThemeTh = { textAlign:'left',padding:'5px 6px',fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.4px',color:'#64748B' };

function ApparelSizeGrid({ sizes, system, onChange, margin, folded }) {
  const rawRows = Array.isArray(sizes) ? sizes : [];
  const totalQty  = rawRows.reduce((s,r)=>s+(Number(r.qty)||0),0);
  const isCustom  = system === 'custom';
  // Display smallest → largest for fixed-size (supplier) grids; keep the user's
  // order for custom grids. Edits target the row by identity, not display index,
  // so sorting can never update the wrong row.
  const rows = isCustom ? rawRows : [...rawRows].sort((a,b)=>sizeRank(a.label)-sizeRank(b.label));
  const upd    = (row, field, val) => onChange(rawRows.map(r => r === row ? { ...r, [field]: val } : r));
  const addRow = () => onChange([...rawRows, { label:'', cost:'', qty:'' }]);
  const rmRow  = (row) => onChange(rawRows.filter(r => r !== row));
  const cell = { border:'1px solid #E2E8F0',borderRadius:5,padding:'3px 6px',fontSize:11,
    fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',width:'100%',boxSizing:'border-box',textAlign:'right' };
  return (
    <div style={{ marginTop:6,border:'1px solid #E2E8F0',borderRadius:8,overflow:'hidden' }}>
      <table style={{ borderCollapse:'collapse',width:'100%',fontSize:11 }}>
        <thead>
          <tr style={{ background:'#F8FAFC' }}>
            <th style={apThemeTh}>Size</th>
            <th style={{ ...apThemeTh, textAlign:'right' }}>Cost/pc</th>
            <th style={{ ...apThemeTh, textAlign:'right' }}>Customer Price (including Decoration Cost and Margin)</th>
            <th style={{ ...apThemeTh, textAlign:'right' }}>Qty</th>
            {isCustom && <th style={apThemeTh}></th>}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={isCustom?4:3} style={{ padding:'8px 6px',color:'#94A3B8',fontSize:11 }}>
              No sizes yet{isCustom ? ' — add one below.' : '.'}
            </td></tr>
          )}
          {rows.map((r, i) => (
            <tr key={isCustom ? i : (r.label || i)} style={{ borderTop:'1px solid #F1F5F9' }}>
              <td style={{ padding:'3px 6px' }}>
                {isCustom
                  ? <input value={r.label} onChange={e=>upd(r,'label',e.target.value)} placeholder="Size" style={{ ...cell, textAlign:'left' }}/>
                  : <span style={{ fontWeight:600,color:'#0F172A' }}>{r.label}</span>}
              </td>
              <td style={{ padding:'3px 6px' }}>
                <div style={{ position:'relative' }}>
                  <span style={{ position:'absolute',left:6,top:4,fontSize:10,color:'#94A3B8' }}>$</span>
                  <input value={r.cost} onChange={e=>upd(r,'cost',e.target.value.replace(/[^0-9.]/g,''))}
                    placeholder="0.00" inputMode="decimal" style={{ ...cell, paddingLeft:14 }}/>
                </div>
              </td>
              <td style={{ padding:'3px 6px',textAlign:'right',color:G,fontWeight:600 }}>
                {r.cost !== '' && r.cost != null
                  ? `$${(((Number(r.cost)||0) + (Number(folded)||0)) / (1 - (Number(margin)||40)/100)).toFixed(2)}`
                  : '—'}
              </td>
              <td style={{ padding:'3px 6px' }}>
                <input value={r.qty} onChange={e=>upd(r,'qty',e.target.value.replace(/[^0-9]/g,''))}
                  placeholder="0" inputMode="numeric" style={cell}/>
              </td>
              {isCustom && (
                <td style={{ padding:'3px 6px',textAlign:'center' }}>
                  <button onClick={()=>rmRow(r)} style={{ background:'none',border:'none',color:'#CBD5E1',fontSize:14,cursor:'pointer',padding:0 }}>×</button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background:'#F8FAFC',borderTop:'1px solid #E2E8F0' }}>
            <td style={{ padding:'4px 6px',fontWeight:600,color:'#475569' }}>Total</td>
            <td/>
            <td/>
            <td style={{ padding:'4px 6px',textAlign:'right',fontWeight:700,color:'#0F172A' }}>{totalQty}</td>
            {isCustom && <td/>}
          </tr>
        </tfoot>
      </table>
      {isCustom && (
        <div style={{ padding:'4px 8px' }}>
          <button onClick={addRow} style={{ background:'none',border:'none',color:G,fontSize:11,fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>+ Add size</button>
        </div>
      )}
    </div>
  );
}

// ─── Add apparel garment (manual, non-API) ───────────────────────────────────
// Produces a custom product flagged `_apparel` with a size system. Costs + qty
// per size are entered on the line grid; this modal captures identity + which
// size system so the grid has the right rows.
function AddApparelGarmentModal({ onClose, onAdd }) {
  const [supplier, setSupplier]       = useState('');
  const [name, setName]               = useState('');
  const [color, setColor]             = useState('');
  const [description, setDescription] = useState('');
  const [system, setSystem]           = useState('adult');
  const [image, setImage]             = useState(null);
  const [imgError, setImgError]       = useState(null);
  const [error, setError]             = useState(null);

  const handleFile = async (e) => {
    setImgError(null);
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try { setImage(await readFileAsDataURL(file)); } catch (err) { setImgError(err.message); }
    e.target.value = '';
  };

  const submit = () => {
    setError(null);
    if (!supplier.trim() || !name.trim()) { setError('Blank supplier and garment name are required'); return; }
    const id = `APPAREL-${Math.random().toString(36).slice(2,11)}`;
    const product = {
      id,
      supplier_code: 'CUSTOM',
      supplier_name: supplier.trim(),
      name: name.trim(),
      category: 'Apparel',
      description: description.trim() || null,
      client_color: color.trim() || null,
      colors: color.trim() ? [{ name: color.trim() }] : [],
      // Net is derived from the size grid (blended), so this tier is a placeholder.
      pricing: [{ qty: 1, list: 0, code: 'X' }],
      images: image ? [{ url: image, type: 'primary' }] : [],
      lead_time: null, fob: null, decoration_methods: [],
      min_qty: 1, carton_qty: null, carton_weight: null, dimensions: null,
      _custom: true, _apparel: true, size_system: system, sizes: blankSizes(system),
    };
    onAdd(product);
    onClose();
  };

  const inp = { width:'100%',border:'1px solid #E2E8F0',borderRadius:8,padding:'9px 11px',
    fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',boxSizing:'border-box' };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:120,
      display:'flex',alignItems:'flex-start',justifyContent:'center',padding:24,overflowY:'auto',backdropFilter:'blur(4px)' }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:'#fff',borderRadius:16,width:'100%',maxWidth:560,overflow:'hidden',marginTop:24 }}>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'18px 24px',borderBottom:'1px solid #E2E8F0' }}>
          <div style={{ fontSize:16,fontWeight:600,color:'#0F172A' }}>Add apparel garment</div>
          <button onClick={onClose} style={{ background:'none',border:'none',fontSize:22,color:'#94A3B8',cursor:'pointer',lineHeight:1 }}>×</button>
        </div>
        <div style={{ padding:24 }}>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
            <div>
              <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Blank supplier *</label>
              <input value={supplier} onChange={e=>setSupplier(e.target.value)} placeholder="e.g. Bella+Canvas" style={inp}/>
            </div>
            <div>
              <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Style / name *</label>
              <input value={name} onChange={e=>setName(e.target.value)} placeholder="3001C Tee" style={inp}/>
            </div>
            <div>
              <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Color</label>
              <input value={color} onChange={e=>setColor(e.target.value)} placeholder="Black" style={inp}/>
            </div>
            <div>
              <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Size system</label>
              <select value={system} onChange={e=>setSystem(e.target.value)} style={inp}>
                {Object.keys(SIZE_SYSTEMS).map(k => <option key={k} value={k}>{SIZE_SYSTEMS[k].label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Description</label>
            <input value={description} onChange={e=>setDescription(e.target.value)} placeholder="Optional" style={inp}/>
          </div>
          <div style={{ fontSize:11,color:'#94A3B8',marginBottom:12 }}>
            You'll enter the cost and quantity per size on the line after adding.
            {system === 'custom' && ' Custom lets you name each size yourself.'}
          </div>
          <div style={{ marginBottom:12 }}>
            <input type="file" accept="image/*" onChange={handleFile} style={{ fontSize:12 }}/>
            {image && <span style={{ fontSize:11,color:G,marginLeft:8 }}>Image attached</span>}
            {imgError && <div style={{ fontSize:11,color:'#B91C1C',marginTop:4 }}>{imgError}</div>}
          </div>
          {error && <div style={{ fontSize:12,color:'#B91C1C',marginBottom:12 }}>{error}</div>}
          <div style={{ display:'flex',justifyContent:'flex-end',gap:10 }}>
            <button onClick={onClose} style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:8,padding:'9px 16px',fontSize:13,color:'#475569',cursor:'pointer',fontFamily:'inherit' }}>Cancel</button>
            <button onClick={submit} style={{ background:G,border:'none',borderRadius:8,padding:'9px 20px',fontSize:13,color:'#fff',fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>Add garment</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomItemModal({ onClose, onAdd }) {
  const [supplier, setSupplier] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [netCost, setNetCost] = useState('');
  const [minQty, setMinQty] = useState('');
  const [decoration, setDecoration] = useState('');
  const [leadTime, setLeadTime] = useState('');
  const [image, setImage] = useState(null);
  const [imgError, setImgError] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    setImgError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await readFileAsDataURL(file);
      setImage(dataUrl);
    } catch (err) {
      setImgError(err.message);
    }
    e.target.value = '';
  };

  const submit = () => {
    setError(null);
    if (!supplier.trim() || !name.trim() || !netCost) {
      setError('Supplier, product name, and net cost are required');
      return;
    }
    const cost = parseFloat(netCost);
    if (isNaN(cost) || cost < 0) {
      setError('Net cost must be a positive number');
      return;
    }
    // Build a fake product object that looks like a regular catalog item.
    // The presentation engine doesn't care that it's custom — it'll render the same.
    const id = `CUSTOM-${Math.random().toString(36).slice(2,11)}`;
    const product = {
      id,
      supplier_code: 'CUSTOM',
      supplier_name: supplier.trim(),
      name: name.trim(),
      category: 'Custom',
      description: description.trim() || null,
      colors: [],
      // Single pricing tier with the net cost. Discount code 'X' = 0% off (it's already net).
      pricing: [{ qty: minQty ? parseInt(minQty,10) : 1, list: cost, code: 'X' }],
      images: image ? [{ url: image, type: 'primary' }] : [],
      lead_time: leadTime.trim() || null,
      fob: null,
      decoration_methods: decoration.trim() ? decoration.split(',').map(s=>s.trim()).filter(Boolean) : [],
      min_qty: minQty ? parseInt(minQty,10) : 1,
      carton_qty: null,
      carton_weight: null,
      dimensions: null,
      _custom: true,
    };
    onAdd(product);
    onClose();
  };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:120,
      display:'flex',alignItems:'flex-start',justifyContent:'center',padding:24,overflowY:'auto',backdropFilter:'blur(4px)' }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:'#fff',borderRadius:16,width:'100%',maxWidth:560,overflow:'hidden',marginTop:24 }}>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'18px 24px',borderBottom:'1px solid #E2E8F0' }}>
          <div>
            <div style={{ fontSize:16,fontWeight:600,color:'#0F172A' }}>Add custom item</div>
            <div style={{ fontSize:12,color:'#94A3B8',marginTop:2 }}>For products outside your synced supplier catalog</div>
          </div>
          <button onClick={onClose} style={{ background:'none',border:'none',cursor:'pointer',fontSize:22,color:'#94A3B8',padding:'0 4px',fontFamily:'inherit' }}>×</button>
        </div>
        <div style={{ padding:24 }}>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14 }}>
            <div>
              <label style={{ fontSize:11,fontWeight:500,color:'#374151',marginBottom:5,display:'block' }}>
                Supplier <span style={{ color:'#EF4444' }}>*</span>
              </label>
              <input value={supplier} onChange={e=>setSupplier(e.target.value)} placeholder="e.g., SanMar"
                style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:8,padding:'8px 12px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',boxSizing:'border-box' }}/>
            </div>
            <div>
              <label style={{ fontSize:11,fontWeight:500,color:'#374151',marginBottom:5,display:'block' }}>
                Net cost (USD) <span style={{ color:'#EF4444' }}>*</span>
              </label>
              <input type="number" step="0.01" min="0" value={netCost} onChange={e=>setNetCost(e.target.value)} placeholder="0.00"
                style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:8,padding:'8px 12px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',boxSizing:'border-box' }}/>
            </div>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:11,fontWeight:500,color:'#374151',marginBottom:5,display:'block' }}>
              Product name <span style={{ color:'#EF4444' }}>*</span>
            </label>
            <input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g., Embroidered Polo Shirt"
              style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:8,padding:'8px 12px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',boxSizing:'border-box' }}/>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:11,fontWeight:500,color:'#374151',marginBottom:5,display:'block' }}>Description</label>
            <textarea value={description} onChange={e=>setDescription(e.target.value)} rows={2}
              placeholder="Optional details about the product…"
              style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:8,padding:'8px 12px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',resize:'vertical',boxSizing:'border-box' }}/>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14 }}>
            <div>
              <label style={{ fontSize:11,fontWeight:500,color:'#374151',marginBottom:5,display:'block' }}>Minimum quantity</label>
              <input type="number" min="1" value={minQty} onChange={e=>setMinQty(e.target.value)} placeholder="1"
                style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:8,padding:'8px 12px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',boxSizing:'border-box' }}/>
            </div>
            <div>
              <label style={{ fontSize:11,fontWeight:500,color:'#374151',marginBottom:5,display:'block' }}>Lead time</label>
              <input value={leadTime} onChange={e=>setLeadTime(e.target.value)} placeholder="e.g., 10 business days"
                style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:8,padding:'8px 12px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',boxSizing:'border-box' }}/>
            </div>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:11,fontWeight:500,color:'#374151',marginBottom:5,display:'block' }}>
              Decoration methods <span style={{ color:'#94A3B8',fontWeight:400 }}>(comma-separated)</span>
            </label>
            <input value={decoration} onChange={e=>setDecoration(e.target.value)} placeholder="e.g., Embroidery, Heat Transfer"
              style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:8,padding:'8px 12px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',boxSizing:'border-box' }}/>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:11,fontWeight:500,color:'#374151',marginBottom:5,display:'block' }}>Product image</label>
            {image ? (
              <div style={{ position:'relative',width:'fit-content' }}>
                <img src={image} alt="Product"
                  style={{ maxWidth:160,maxHeight:120,borderRadius:8,border:'1px solid #E2E8F0',background:'#F8FAFC',padding:6 }}/>
                <button onClick={()=>setImage(null)} aria-label="Remove image"
                  style={{ position:'absolute',top:-8,right:-8,width:22,height:22,borderRadius:'50%',
                    border:'1px solid #E2E8F0',background:'#fff',cursor:'pointer',fontSize:14,
                    color:'#64748B',display:'flex',alignItems:'center',justifyContent:'center',padding:0,
                    boxShadow:'0 2px 6px rgba(0,0,0,0.08)',fontFamily:'inherit' }}>×</button>
              </div>
            ) : (
              <>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display:'none' }}/>
                <button onClick={()=>fileRef.current?.click()}
                  style={{ padding:'14px 16px',border:'1px dashed #CBD5E1',borderRadius:8,
                    background:'#F8FAFC',cursor:'pointer',fontSize:12,color:'#64748B',fontFamily:'inherit',
                    display:'flex',alignItems:'center',gap:8 }}>
                  <span style={{ fontSize:16 }}>📷</span> Upload image (optional, up to 2MB)
                </button>
              </>
            )}
            {imgError && <div style={{ fontSize:11,color:'#EF4444',marginTop:6 }}>{imgError}</div>}
          </div>
          {error && <div style={{ fontSize:12,color:'#EF4444',marginTop:8 }}>{error}</div>}
        </div>
        <div style={{ padding:'14px 24px',borderTop:'1px solid #E2E8F0',display:'flex',gap:10,justifyContent:'flex-end' }}>
          <button onClick={onClose}
            style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:8,padding:'10px 18px',fontSize:13,cursor:'pointer',fontFamily:'inherit',color:'#64748B' }}>
            Cancel
          </button>
          <button onClick={submit}
            style={{ background:G,border:'none',borderRadius:8,padding:'10px 22px',color:'#fff',fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'inherit' }}>
            Add to Presentation
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductRow({ product, itemNo, qty, margin, note, netOverride, mockup, art, charges, colorOverride,
  showAllColors, showPriceGrid, tierOverrides, thirdPartyDeco,
  apparelSizes, onSetApparelSizes,
  shipping, onSetShipping,
  onMargin, onQty, onNote, onSetNetOverride, onUploadMockup, onRemoveMockup, onSetArt,
  onAddCharge, onChangeChargePrice, onChangeChargeRunQty, onRemoveCharge, onOverrideRunCost,
  onChangeClientColor, onToggleShowAllColors, onToggleShowPriceGrid, onToggleThirdPartyDeco,
  onSetTierMargin, onSetTierPriceOverride, onResetAllTiers,
  onRemove, getToken, showToast, clientId, folded }) {
  // Track product locally so InventoryPanel's "Refresh stock" call updates
  // this row in place. The parent presentation state still holds the original
  // product; this is just for live inventory display while editing.
  const [p, setP] = useState(product);
  // A line is apparel if flagged OR if it has a size grid. The _apparel flag
  // rides on the shared cart object and can be lost when that cart is rebuilt;
  // the presence of size-grid data (Presentations-local state) is the reliable
  // signal, so we treat either as apparel.
  const isApparel = !!(p._apparel || (Array.isArray(apparelSizes) && apparelSizes.length > 0));
  // Whether the per-product shipping estimator panel is open. Independent
  // toggle per product since each product has its own carton data / origin.
  const [showShip, setShowShip] = useState(false);
  const [editingNet, setEditingNet] = useState(false);
  const [draftNet, setDraftNet] = useState('');
  // Pick the tier matching the current quantity (not always the first one).
  const tiers = p.pricing || [];
  let activeTier = tiers[0] || {};
  for (const t of tiers) {
    const breakQty = t.qty || t.q || 0;
    if (qty >= breakQty) activeTier = t;
    else break;
  }
  const listNet = net(activeTier.list || activeTier.l || 0, activeTier.code || activeTier.c || 'A');
  const isOverridden = typeof netOverride === 'number' && !isNaN(netOverride);
  // Folded-in decoration cost (e.g. heat-transfer material ordered from one
  // vendor and folded into the garment via a job's "Fold this cost into the
  // garment" checkbox) — added here too, not just in the parent's
  // effectiveNet() used for the saved/quoted data. Without this, the on-
  // screen price a rep is looking at while quoting doesn't match what
  // actually gets saved and charged — confusing regardless of which number
  // is "more right," since they need to match.
  const n = (isOverridden ? netOverride : listNet) + (Number(folded) || 0);
  const s = sell(n, margin);
  const requestedColor = p.client_color;
  // Distributor's override wins if set; otherwise use the customer's original choice.
  const effectiveClientColor = colorOverride !== undefined ? colorOverride : requestedColor;
  // Top-left thumbnail: mockup wins, then primary product image, then null (emoji fallback).
  const topImage = mockup
    || (p.images||[]).find(i => i.type === 'primary')?.url
    || (p.images||[])[0]?.url
    || null;

  const startEdit = () => {
    setDraftNet(String(n.toFixed(2)));
    setEditingNet(true);
  };
  const commitEdit = () => {
    const v = parseFloat(draftNet);
    if (!isNaN(v) && v >= 0) {
      // If they entered the list net exactly, clear the override (back to "default")
      onSetNetOverride(Math.abs(v - listNet) < 0.005 ? null : +v.toFixed(2));
    }
    setEditingNet(false);
  };
  const cancelEdit = () => { setEditingNet(false); };
  const resetOverride = () => onSetNetOverride(null);

  return (
    <div style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:10,padding:14,marginBottom:10 }}>
      <div style={{ display:'grid',gridTemplateColumns:'60px 1fr auto',gap:14,alignItems:'start' }}>
        <div style={{ width:60,height:60,borderRadius:8,background:'#F8FAFC',
          border:'1px solid #E2E8F0',display:'flex',alignItems:'center',justifyContent:'center',
          fontSize:24,overflow:'hidden',flexShrink:0 }}>
          {topImage ? (
            <img src={topImage} alt={p.name}
              style={{ maxWidth:'100%',maxHeight:'100%',objectFit:'contain' }}/>
          ) : (
            <span>{EMOJIS[p.category]||'📦'}</span>
          )}
        </div>
        <div>
          <div style={{ fontSize:13,fontWeight:500,color:'#0F172A',marginBottom:2 }}>{itemNo != null ? <span style={{ color:'#94A3B8',fontWeight:600 }}>#{itemNo} </span> : null}{p.name}</div>
          <div style={{ fontSize:11,color:'#94A3B8',marginBottom:8 }}>
            <SupplierPopover product={p}>{p.supplier_name}</SupplierPopover>
            {p.supplier_sku && <span> · #{p.supplier_sku}</span>}
            <span> · Min {p.min_qty}</span>
          </div>
          <InventoryPanel product={p} getToken={getToken} onRefreshed={setP}/>
          {(requestedColor || (p.colors||[]).length > 0) && (
            <ClientColorPicker
              value={effectiveClientColor}
              originalValue={requestedColor}
              availableColors={p.colors || []}
              onChange={onChangeClientColor}/>
          )}
          {isApparel ? (
            <div style={{ marginBottom:6 }}>
              <div style={{ fontSize:11,color:'#94A3B8',marginBottom:2 }}>
                Sizes &amp; quantities <span style={{ color:'#CBD5E1' }}>·</span> {(SIZE_SYSTEMS[p.size_system]||{}).label || 'Custom'}
              </div>
              <ApparelSizeGrid sizes={apparelSizes || []} system={p.size_system}
                onChange={onSetApparelSizes} margin={margin} folded={folded}/>
            </div>
          ) : null}
          <div style={{ display:'flex',gap:12,alignItems:'center',flexWrap:'wrap' }}>
            {!isApparel && (
            <div style={{ display:'flex',alignItems:'center',gap:6 }}>
              <span style={{ fontSize:11,color:'#94A3B8' }}>Qty:</span>
              <input type="number" min="1" value={qty} onChange={e=>onQty(Math.max(1,+e.target.value))}
                style={{ width:64,border:'1px solid #E2E8F0',borderRadius:6,padding:'3px 6px',fontSize:12,fontFamily:'inherit',textAlign:'center',outline:'none',color:'#0F172A' }}/>
            </div>
            )}
            <div style={{ display:'flex',alignItems:'center',gap:6 }}>
              <span style={{ fontSize:11,color:'#94A3B8' }}>Margin:</span>
              <input type="number" min="1" max="80" value={margin} onChange={e=>onMargin(Math.min(80,Math.max(1,+e.target.value)))}
                style={{ width:52,border:'1px solid #E2E8F0',borderRadius:6,padding:'3px 6px',fontSize:12,fontFamily:'inherit',textAlign:'center',outline:'none',color:'#0F172A' }}/>
              <span style={{ fontSize:11,color:'#94A3B8' }}>%</span>
            </div>
            {!isApparel && (
            <div style={{ display:'flex',alignItems:'center',gap:6,fontSize:11,color:'#94A3B8' }}>
              <span>Net</span>
              {editingNet ? (
                <>
                  <span style={{ color:'#94A3B8' }}>$</span>
                  <input
                    type="number" step="0.01" min="0" autoFocus
                    value={draftNet}
                    onChange={e=>setDraftNet(e.target.value)}
                    onBlur={commitEdit}
                    onKeyDown={e=>{
                      if (e.key === 'Enter') { e.preventDefault(); commitEdit(); }
                      else if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                    }}
                    style={{ width:64,border:`1px solid ${G}`,borderRadius:5,padding:'2px 6px',
                      fontSize:11,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff' }}/>
                </>
              ) : (
                <button onClick={startEdit} title="Click to override supplier net price"
                  style={{ background: isOverridden ? `${G}18` : 'transparent',
                    border:`1px ${isOverridden ? 'solid' : 'dashed'} ${isOverridden ? G : '#CBD5E1'}`,
                    borderRadius:5,padding:'1px 7px',fontSize:11,
                    color: isOverridden ? '#059669' : '#475569',
                    cursor:'pointer',fontFamily:'inherit',fontWeight: isOverridden ? 600 : 400 }}>
                  ${n.toFixed(2)}
                  {isOverridden && <span style={{ marginLeft:4,fontSize:9,fontWeight:600 }}>✏</span>}
                </button>
              )}
              {isOverridden && !editingNet && (
                <button onClick={resetOverride} title={`Reset to supplier list net ($${listNet.toFixed(2)})`}
                  style={{ background:'none',border:'none',color:'#94A3B8',fontSize:11,cursor:'pointer',padding:'0 2px',fontFamily:'inherit' }}>
                  ↺
                </button>
              )}
              <span style={{ color:G,fontWeight:600 }}>→ {$$(s)}</span>
            </div>
            )}
          </div>
          {!isApparel && isOverridden && !editingNet && (
            <div style={{ fontSize:10,color:'#94A3B8',marginTop:4 }}>
              Custom net: ${n.toFixed(2)} <span style={{ color:'#CBD5E1' }}>·</span> Supplier list net was ${listNet.toFixed(2)}
            </div>
          )}
          <div style={{ display:'flex',gap:14,marginTop:10,flexWrap:'wrap' }}>
            <label style={{ display:'inline-flex',alignItems:'center',gap:6,fontSize:11,color:'#475569',cursor:'pointer' }}>
              <input type="checkbox" checked={!!showAllColors} onChange={e=>onToggleShowAllColors(e.target.checked)}
                style={{ cursor:'pointer' }}/>
              Show all available colors to client
            </label>
            <label style={{ display:'inline-flex',alignItems:'center',gap:6,fontSize:11,color:'#475569',cursor:'pointer' }}>
              <input type="checkbox" checked={!!showPriceGrid} onChange={e=>onToggleShowPriceGrid(e.target.checked)}
                style={{ cursor:'pointer' }}/>
              Show full price grid (qty breaks)
            </label>
          </div>
          {showPriceGrid && (
            <PriceGridPreview
              product={p}
              defaultMargin={margin}
              currentQty={qty}
              tierOverrides={tierOverrides || {}}
              onSetTierMargin={onSetTierMargin}
              onSetTierPriceOverride={onSetTierPriceOverride}
              onResetAll={onResetAllTiers}/>
          )}
          <input value={note} onChange={e=>onNote(e.target.value)} placeholder="Add a note to client (optional)..."
            style={{ marginTop:8,width:'100%',border:'1px solid #E2E8F0',borderRadius:7,padding:'5px 10px',fontSize:11,fontFamily:'inherit',outline:'none',color:'#374151',background:'#F8FAFC' }}/>
          <ProductChargesList charges={charges}
            onChangeClientPrice={onChangeChargePrice}
            onChangeRunQty={onChangeChargeRunQty}
            onOverrideRunCost={onOverrideRunCost}
            onRemove={onRemoveCharge}/>
          <SupplierChargesPanel product={p} productQty={qty}
            currentCharges={charges}
            onAddCharge={onAddCharge}/>
          <MockupButton mockup={mockup}
            onUpload={onUploadMockup}
            onRemove={onRemoveMockup}/>
          <ItemArtButton art={art} onSetArt={onSetArt} clientId={clientId} getToken={getToken} showToast={showToast}/>
          {/* Per-product shipping (June 2026, Push B). Each product ships from
              its own supplier with its own carton data and origin, so shipping
              is configured per item. The configured display + estimator open
              independently per product. */}
          {shipping && shipping.mode ? (
            <div style={{ marginTop:8,background:'#F0FDF4',border:`1px solid ${G}33`,borderRadius:8,padding:'10px 12px' }}>
              <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6 }}>
                <div style={{ fontSize:11,fontWeight:600,color:'#059669',letterSpacing:'0.4px',textTransform:'uppercase' }}>
                  Shipping{shipping.label ? ` — ${shipping.label}` : ''}
                </div>
                <div style={{ display:'flex',gap:6 }}>
                  <button onClick={() => setShowShip(v => !v)}
                    style={{ background:'none',border:'1px solid #E2E8F0',borderRadius:5,padding:'2px 8px',
                      fontSize:10,color:'#475569',cursor:'pointer',fontFamily:'inherit' }}>
                    {showShip ? 'Close' : 'Edit'}
                  </button>
                  <button onClick={() => onSetShipping(null)}
                    style={{ background:'none',border:'1px solid #FECACA',borderRadius:5,padding:'2px 8px',
                      fontSize:10,color:'#DC2626',cursor:'pointer',fontFamily:'inherit' }}>
                    Remove
                  </button>
                </div>
              </div>
              <div style={{ fontSize:11,color:'#475569',lineHeight:1.5 }}>
                {shipping.mode === 'line' ? (
                  <><strong style={{ color:'#0F172A' }}>${Number(shipping.amount).toFixed(2)}</strong> shown as a separate "{shipping.label || 'Shipping'}" line.</>
                ) : (
                  <><strong style={{ color:'#0F172A' }}>${Number(shipping.amount).toFixed(2)}</strong> blended at <strong style={{ color:'#0F172A' }}>${(Number(shipping.amount) / Number(shipping.blendQty || 1)).toFixed(2)}/unit</strong> (qty {shipping.blendQty}).</>
                )}
              </div>
            </div>
          ) : (
            <button onClick={()=>setShowShip(v=>!v)}
              style={{ marginTop:8,background:'none',border:'1px solid #E2E8F0',borderRadius:6,padding:'3px 10px',fontSize:11,color:'#64748B',cursor:'pointer',fontFamily:'inherit' }}>
              {showShip?'Hide shipping':'📦 Estimate shipping'}
            </button>
          )}
          {showShip && (
            <ShippingEstimator
              product={p}
              qty={qty}
              getToken={getToken}
              existing={shipping}
              onApply={(applied) => { onSetShipping(applied); setShowShip(false); }}
              onCancel={() => setShowShip(false)}
            />
          )}
        </div>
        <button onClick={onRemove} style={{ background:'none',border:'none',cursor:'pointer',color:'#94A3B8',fontSize:20,padding:'0 4px' }}>×</button>
      </div>
    </div>
  );
}

// InlineShareLink — small share-link box shown in the builder sidebar when
// a presentation has a share_token. Lets the distributor copy the public URL
// without opening the modal. Kept compact since the sidebar is space-constrained.
function InlineShareLink({ token }) {
  const url = `${window.location.origin}/p/${token}`;
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div style={{ marginBottom:16,padding:'10px 12px',background:'#F0FDF4',border:`1px solid ${G}33`,borderRadius:9 }}>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6 }}>
        <div style={{ fontSize:10,fontWeight:600,color:'#059669',letterSpacing:'0.5px',textTransform:'uppercase' }}>
          Client link
        </div>
        <button onClick={copy}
          style={{ background:copied?G:'#fff',border:`1px solid ${G}`,borderRadius:6,padding:'3px 10px',fontSize:11,color:copied?'#fff':G,cursor:'pointer',fontFamily:'inherit',fontWeight:500 }}>
          {copied?'Copied ✓':'Copy'}
        </button>
      </div>
      <div style={{ fontSize:11,color:'#374151',wordBreak:'break-all',lineHeight:1.4 }}>
        {url}
      </div>
    </div>
  );
}

function ShareModal({ token, onClose }) {
  const url = `${window.location.origin}/p/${token}`;
  const [copied,setCopied]=useState(false);
  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(4px)' }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:'#fff',borderRadius:16,padding:32,maxWidth:480,width:'90%' }}>
        <div style={{ fontSize:18,fontWeight:600,color:'#0F172A',marginBottom:6 }}>Ready to send</div>
        <div style={{ fontSize:13,color:'#64748B',marginBottom:20,lineHeight:1.6 }}>
          Share this link. Client sees your branded proposal with an Approve button. No pricing, no supplier names, no {BRAND.name} branding visible.
        </div>
        <div style={{ background:'#F8FAFC',border:'1px solid #E2E8F0',borderRadius:8,padding:'10px 14px',display:'flex',gap:10,alignItems:'center',marginBottom:16 }}>
          <span style={{ flex:1,fontSize:12,color:'#374151',wordBreak:'break-all' }}>{url}</span>
          <button onClick={()=>{navigator.clipboard.writeText(url);setCopied(true);setTimeout(()=>setCopied(false),2500);}}
            style={{ background:copied?G:'#0F172A',border:'none',borderRadius:6,padding:'6px 14px',color:'#fff',fontSize:12,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap' }}>
            {copied?'Copied':'Copy'}
          </button>
        </div>
        <button onClick={onClose} style={{ background:'#F1F5F9',border:'none',borderRadius:8,padding:'9px 20px',fontSize:13,cursor:'pointer',fontFamily:'inherit',color:'#374151' }}>Done</button>
      </div>
    </div>
  );
}

// SaveModeModal — appears when Save Draft is clicked on an existing presentation.
// Two buttons: "Update existing" (primary, autofocused — common case) and "Save
// as new" (secondary, creates a new presentation row that copies current state).
// Closes on backdrop click or Escape. Disabled buttons while save is in flight.
function SaveModeModal({ presentationName, saving, onUpdate, onSaveAsNew, onCancel }) {
  const primaryRef = useRef(null);
  useEffect(() => {
    primaryRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [saving, onCancel]);
  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(4px)' }}
      onClick={e=>!saving && e.target===e.currentTarget && onCancel()}>
      <div style={{ background:'#fff',borderRadius:16,padding:28,maxWidth:440,width:'90%' }}>
        <div style={{ fontSize:18,fontWeight:600,color:'#0F172A',marginBottom:8 }}>Save changes</div>
        <div style={{ fontSize:13,color:'#64748B',marginBottom:22,lineHeight:1.6 }}>
          You're editing{presentationName ? <> <strong style={{ color:'#0F172A' }}>"{presentationName}"</strong></> : ' an existing presentation'}.
          {' '}Update it, or save as a new presentation?
        </div>
        <div style={{ display:'flex',flexDirection:'column',gap:10,marginBottom:14 }}>
          <button ref={primaryRef} onClick={onUpdate} disabled={saving}
            style={{ background:G,border:'none',borderRadius:9,padding:'12px 16px',color:'#fff',fontSize:13,fontWeight:500,cursor:saving?'not-allowed':'pointer',fontFamily:'inherit',opacity:saving?0.6:1,textAlign:'left' }}>
            <div>Update existing</div>
            <div style={{ fontSize:11,fontWeight:400,opacity:0.85,marginTop:2 }}>Replaces the current presentation. Existing share link keeps working.</div>
          </button>
          <button onClick={onSaveAsNew} disabled={saving}
            style={{ background:'#fff',border:`1px solid ${G}`,borderRadius:9,padding:'12px 16px',color:G,fontSize:13,fontWeight:500,cursor:saving?'not-allowed':'pointer',fontFamily:'inherit',opacity:saving?0.6:1,textAlign:'left' }}>
            <div>Save as new</div>
            <div style={{ fontSize:11,fontWeight:400,opacity:0.85,marginTop:2,color:'#475569' }}>Creates a separate presentation. Original stays unchanged with its own share link.</div>
          </button>
        </div>
        <button onClick={onCancel} disabled={saving}
          style={{ background:'transparent',border:'none',padding:'8px 4px',color:'#64748B',fontSize:12,cursor:saving?'not-allowed':'pointer',fontFamily:'inherit' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Saved presentations list (visible when cart is empty) ──────────────────
// Shows the distributor's existing presentations. Searchable, filterable by status,
// with archive/restore. Selecting an item shows its share link + lets them download
// the PDF. Editing an existing presentation back into the builder is a future feature.
function SavedPresentationsList({ getToken, showToast, onStartNew, onEdit }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [busy, setBusy] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const t = await getToken();
      const params = new URLSearchParams();
      if (showArchived) params.set('archived', 'true');
      if (search.trim()) params.set('q', search.trim());
      const r = await fetch(`/api/presentations?${params}`, { headers: { Authorization: `Bearer ${t}` }});
      if (!r.ok) throw new Error(`Server returned ${r.status}`);
      setList(await r.json());
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [getToken, showArchived, search]);

  useEffect(() => {
    const t = setTimeout(refresh, 250);
    return () => clearTimeout(t);
  }, [refresh]);

  const updatePresentation = async (id, updates) => {
    setBusy(id);
    try {
      const t = await getToken();
      const r = await fetch(`/api/presentations/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type':'application/json', Authorization: `Bearer ${t}` },
        body: JSON.stringify(updates),
      });
      if (!r.ok) throw new Error('Server error');
      refresh();
      showToast('Updated');
    } catch (e) {
      showToast('Failed: ' + e.message, 'error');
    } finally {
      setBusy(null);
    }
  };

  const filtered = statusFilter === 'all' ? list : list.filter(p => p.status === statusFilter);

  return (
    <div style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:12,padding:24,marginBottom:24 }}>
      {/* Header */}
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:16,flexWrap:'wrap',gap:12 }}>
        <div>
          <div style={{ fontSize:16,fontWeight:600,color:'#0F172A' }}>
            Your Presentations {showArchived && <span style={{ fontSize:12,color:'#94A3B8',fontWeight:400 }}>· archived</span>}
          </div>
          <div style={{ fontSize:13,color:'#64748B' }}>
            {showArchived ? 'Past proposals you\'ve archived.' : 'Manage your active proposals.'}
          </div>
        </div>
        <div style={{ display:'flex',gap:8 }}>
          <button onClick={()=>setShowArchived(v=>!v)}
            style={{ background: showArchived ? BRAND.colors.dark : '#fff',
              color: showArchived ? '#fff' : '#475569',
              border:`1px solid ${showArchived ? BRAND.colors.dark : '#E2E8F0'}`,
              borderRadius:8,padding:'6px 14px',fontSize:12,cursor:'pointer',fontFamily:'inherit',fontWeight:500 }}>
            {showArchived ? '← Active' : '📦 Archived'}
          </button>
          <button onClick={onStartNew}
            style={{ background:G,border:'none',borderRadius:8,padding:'7px 16px',color:'#fff',
              fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit' }}>
            + New presentation
          </button>
        </div>
      </div>

      {/* Search + filter */}
      <div style={{ display:'flex',gap:8,marginBottom:14,alignItems:'center',flexWrap:'wrap' }}>
        <div style={{ position:'relative',flex:1,minWidth:240,maxWidth:480 }}>
          <span style={{ position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',fontSize:13,color:'#94A3B8',pointerEvents:'none' }}>🔍</span>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search by name, client name, or email…"
            style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:8,
              padding:'9px 14px 9px 36px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',boxSizing:'border-box' }}/>
          {search && (
            <button onClick={()=>setSearch('')} aria-label="Clear search"
              style={{ position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',
                background:'none',border:'none',cursor:'pointer',color:'#94A3B8',fontSize:16,padding:'0 4px',fontFamily:'inherit' }}>×</button>
          )}
        </div>
        {!showArchived && (
          <div style={{ display:'flex',gap:5,flexWrap:'wrap' }}>
            {['all','draft','sent','approved','changes_requested','won','lost'].map(f => (
              <button key={f} onClick={()=>setStatusFilter(f)}
                style={{ background: statusFilter===f ? BRAND.colors.dark : '#fff',
                  color: statusFilter===f ? '#fff' : '#64748B',
                  border:`1px solid ${statusFilter===f ? BRAND.colors.dark : '#E2E8F0'}`,
                  borderRadius:14,padding:'4px 11px',fontSize:11,cursor:'pointer',fontFamily:'inherit',
                  textTransform:'capitalize' }}>
                {f === 'all' ? 'All' : f.replace('_',' ')}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Body */}
      {loading && <div style={{ padding:32,textAlign:'center',color:'#94A3B8',fontSize:13 }}>Loading...</div>}

      {!loading && filtered.length === 0 && (
        <div style={{ padding:'36px 16px',textAlign:'center',background:'#F8FAFC',borderRadius:8,border:'1px dashed #CBD5E1' }}>
          <div style={{ fontSize:32,marginBottom:8 }}>{showArchived ? '📦' : (search ? '🔍' : '📋')}</div>
          <div style={{ fontSize:13,fontWeight:500,color:'#374151',marginBottom:4 }}>
            {search
              ? `No presentations match "${search}"`
              : showArchived
                ? 'No archived presentations'
                : statusFilter === 'all'
                  ? 'No presentations yet'
                  : `No "${statusFilter.replace('_',' ')}" presentations`}
          </div>
          <div style={{ fontSize:12,color:'#94A3B8',lineHeight:1.6 }}>
            {showArchived ? 'When you archive a presentation, it lives here for future reference.' : 'Build a new presentation by adding products below.'}
          </div>
        </div>
      )}

      {filtered.map(p => {
        const stStyle = STATUS_STYLES_PRESENTATION[p.status] || STATUS_STYLES_PRESENTATION.draft;
        const productCount = Array.isArray(p.products) ? p.products.length : 0;
        const shareUrl = p.share_token ? `${window.location.origin}/p/${p.share_token}` : null;
        // Approved/changes-requested are client DECISIONS, not just status —
        // make them visually impossible to miss scanning the list, not just
        // a small badge easy to skim past.
        const isDecision = p.status === 'approved' || p.status === 'changes_requested';
        return (
          <div key={p.id} style={{ border:'1px solid #E2E8F0',borderRadius:10,padding:'12px 14px',marginBottom:8,
            opacity: p.archived ? 0.85 : 1, background: isDecision ? stStyle.bg : '#fff',
            borderLeft: isDecision ? `4px solid ${stStyle.color}` : '1px solid #E2E8F0' }}>
            <div style={{ display:'flex',gap:12,alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap' }}>
              <div style={{ minWidth:0,flex:1 }}>
                <div style={{ fontSize:14,fontWeight:600,color:'#0F172A',marginBottom:3 }}>{p.name}</div>
                <div style={{ fontSize:12,color:'#64748B' }}>
                  {p.client_name || 'No client'}
                  {p.client_email && <> · {p.client_email}</>}
                  {productCount > 0 && <> · {productCount} item{productCount===1?'':'s'}</>}
                </div>
                <div style={{ fontSize:11,color:'#94A3B8',marginTop:3 }}>
                  Created {fmtDate(p.created_at)}
                  {p.updated_at && p.updated_at !== p.created_at && <> · Updated {fmtDate(p.updated_at)}</>}
                </div>
                {p.status === 'changes_requested' && p.changes_message && (
                  <div style={{ fontSize:12,color:'#78350F',marginTop:8,padding:'8px 10px',background:'#fff',
                    border:'1px solid #FCD34D',borderRadius:6,fontStyle:'italic' }}>
                    "{p.changes_message}"
                  </div>
                )}
              </div>
              <div style={{ display:'flex',flexDirection:'column',alignItems:'flex-end',gap:5 }}>
                <div style={{ display:'flex',gap:5,alignItems:'center' }}>
                  {p.archived && (
                    <span style={{ background:'#F1F5F9',color:'#64748B',fontSize:10,fontWeight:600,padding:'2px 8px',borderRadius:8,textTransform:'uppercase',letterSpacing:'0.5px' }}>
                      Archived
                    </span>
                  )}
                  <span style={{ background:stStyle.bg,color:stStyle.color,fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:10,textTransform:'capitalize',
                    border: isDecision ? `1px solid ${stStyle.color}` : 'none' }}>
                    {p.status === 'approved' ? '✓ ' : p.status === 'changes_requested' ? '✎ ' : ''}{stStyle.label}
                  </span>
                </div>
              </div>
            </div>

            {/* Inline actions */}
            <div style={{ display:'flex',gap:6,marginTop:10,alignItems:'center',flexWrap:'wrap' }}>
              {/* Edit button — loads back into builder */}
              {!p.archived && onEdit && (
                <button onClick={()=>onEdit(p.id)} disabled={busy === p.id}
                  style={{ background:G,border:'none',borderRadius:6,padding:'5px 12px',
                    fontSize:11,color:'#fff',fontWeight:600,cursor:'pointer',fontFamily:'inherit' }}>
                  ✏ Edit
                </button>
              )}
              {/* Status dropdown */}
              {!p.archived && (
                <select value={p.status||'draft'} onChange={e=>updatePresentation(p.id, { status: e.target.value })}
                  disabled={busy === p.id}
                  style={{ fontSize:11,padding:'4px 8px',border:'1px solid #E2E8F0',borderRadius:6,
                    background:'#fff',color:'#475569',fontFamily:'inherit',cursor:'pointer' }}>
                  <option value="draft">Set status: draft</option>
                  <option value="sent">Set status: sent</option>
                  <option value="approved">Set status: approved</option>
                  <option value="changes_requested">Set status: changes requested</option>
                  <option value="won">Set status: won</option>
                  <option value="lost">Set status: lost</option>
                </select>
              )}
              {shareUrl && (
                <button onClick={()=>{
                    navigator.clipboard.writeText(shareUrl);
                    showToast('Link copied to clipboard');
                  }}
                  style={{ background:'none',border:'1px solid #E2E8F0',borderRadius:6,padding:'4px 10px',
                    fontSize:11,color:'#475569',cursor:'pointer',fontFamily:'inherit' }}>
                  📋 Copy link
                </button>
              )}
              {shareUrl && (
                <a href={shareUrl} target="_blank" rel="noreferrer"
                  title="Opens the client-facing version in a new tab"
                  style={{ background:'none',border:'1px solid #E2E8F0',borderRadius:6,padding:'4px 10px',
                    fontSize:11,color:'#475569',textDecoration:'none',fontFamily:'inherit' }}>
                  🔗 Client view
                </a>
              )}
              <button onClick={()=>updatePresentation(p.id, { archived: !p.archived })}
                disabled={busy === p.id}
                style={{ background:'none',border:'1px solid #E2E8F0',borderRadius:6,padding:'4px 10px',
                  fontSize:11,color:'#64748B',cursor:'pointer',fontFamily:'inherit',marginLeft:'auto' }}>
                {p.archived ? '↺ Restore' : '📦 Archive'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const STATUS_STYLES_PRESENTATION = {
  draft:              { bg:'#F1F5F9',  color:'#475569',  label:'draft' },
  sent:               { bg:'#DBEAFE',  color:'#1E40AF',  label:'sent' },
  approved:           { bg:`${G}18`,   color:'#059669',  label:'approved' },
  changes_requested:  { bg:'#FEF3C7',  color:'#92400E',  label:'changes requested' },
  won:                { bg:'#D1FAE5',  color:'#065F46',  label:'won' },
  lost:               { bg:'#FEE2E2',  color:'#991B1B',  label:'lost' },
};

const fmtDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString(undefined, { month:'short', day:'numeric', year:'numeric' });
  } catch { return dateStr; }
};

// Order-level panel: create combined-decoration jobs. One imprint shared by
// several garments, priced at their combined quantity, setup charged once.
function DecorationJobsPanel({ jobs, matrices, apparelProducts, qtyOf, onChange, getToken, showToast, clientId }) {
  const [draft, setDraft] = useState(null);
  const [uploadingArt, setUploadingArt] = useState({}); // memberId -> bool, while a PUT is in flight
  const inp = { border:'1px solid #E2E8F0',borderRadius:6,padding:'5px 9px',fontSize:12,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',boxSizing:'border-box' };
  const mtx = (id) => (matrices||[]).find(m=>m.id===id) || null;

  const newDraft = () => setDraft({ id:`job_${Date.now()}_${Math.random().toString(36).slice(2,7)}`, matrixId:'', locations:[''], count:'', members:[], feeIndexes:[] });
  const editJob  = (j) => setDraft({
    ...j,
    locations: (Array.isArray(j.locations) && j.locations.length) ? [...j.locations] : (j.location ? [j.location] : ['']),
    members: (Array.isArray(j.members) && j.members.length)
      ? j.members.map(m => ({ id:m.id, art:m.art||'', ink:m.ink||'', artFileId:m.artFileId||null, artFileName:m.artFileName||null, artFileUrl:m.artFileUrl||null, artFileMime:m.artFileMime||null }))
      : (j.memberIds||[]).map(id => ({ id, art:'', ink:'', artFileId:null, artFileName:null, artFileUrl:null })),
    feeIndexes:[...(j.feeIndexes||[])],
  });
  const commit = () => {
    const locs = (draft.locations||[]).map(l=>(l||'').trim()).filter(Boolean);
    if (!draft.matrixId || !draft.members.length || !locs.length) return;
    const man = isManualJob(draft);
    // A manual job with neither a run cost nor a setup would price at zero and
    // silently produce a free decoration — refuse it the same way a missing
    // matrix is refused.
    if (man && !(manualRunCostOf(draft) > 0 || manualSetupCostOf(draft) > 0)) return;
    const clean = { ...draft, locations: locs, location: locs[0], memberIds: undefined,  // keep location for back-compat
      ...(man ? { manual: true, matrixId: MANUAL_MATRIX_ID, feeIndexes: [] } : {}) };
    onChange(jobs.some(j=>j.id===draft.id) ? jobs.map(j=>j.id===draft.id?clean:j) : [...jobs, clean]);
    setDraft(null);
  };
  const removeJob = (id) => onChange(jobs.filter(j=>j.id!==id));
  const memIndex = (id) => draft ? draft.members.findIndex(m=>m.id===id) : -1;
  const toggleMember = (id, on) => setDraft(d => ({ ...d, members: on ? [...d.members, {id, art:'', ink:'', artFileId:null, artFileName:null, artFileUrl:null, artFileMime:null}] : d.members.filter(m=>m.id!==id) }));
  const setMember = (id, field, val) => setDraft(d => ({ ...d, members: d.members.map(m=>m.id===id?{...m,[field]:val}:m) }));
  // Normalize an art label the same way everywhere it's compared — trimmed,
  // case-insensitive, defaulting to 'Art' (matches the pricing/grouping logic
  // in computeJobCharges, which treats blank art as a single implicit group).
  const artKeyOf = (m) => (m.art || 'Art').trim().toLowerCase();
  // Apply an art-file patch to every member sharing a given art label, not
  // just one. This is the actual fix for the "why do I have to upload the
  // same art twice" bug — art belongs to the ART, not to each garment that
  // happens to use it, same as how setup/pricing already groups by art.
  // Special key '__job__' broadcasts to EVERY member regardless of label —
  // the job-level "Attach art for this job" control below uses this, for
  // the common case of one artwork for the whole job (no need to touch each
  // item individually, or even give them matching labels first).
  const setArtForGroup = (artKey, patch) => setDraft(d => ({
    ...d,
    members: d.members.map(m => (artKey === '__job__' || artKeyOf(m) === artKey) ? { ...m, ...patch } : m),
  }));
  const setLoc    = (i, val) => setDraft(d => ({ ...d, locations: (d.locations||['']).map((l,k)=>k===i?val:l) }));
  const addLoc    = () => setDraft(d => ({ ...d, locations: [...(d.locations||['']), ''] }));
  const rmLoc     = (i) => setDraft(d => ({ ...d, locations: (d.locations||['']).filter((_,k)=>k!==i) }));

  // When someone types/changes an art label, if another member in the job
  // already has that same (normalized) label WITH a file attached, inherit
  // it automatically — covers "I typed the label before uploading" and
  // "I renamed to match an existing group" without requiring a re-upload.
  // Never clears an existing file just because the label changed — that
  // would be a surprising, destructive side effect of typing.
  const setMemberArtLabel = (memberId, value) => {
    setDraft(d => {
      const newKey = (value || 'Art').trim().toLowerCase();
      const donor = d.members.find(m => m.id !== memberId && artKeyOf(m) === newKey && m.artFileId);
      return {
        ...d,
        members: d.members.map(m => {
          if (m.id !== memberId) return m;
          const next = { ...m, art: value };
          if (!m.artFileId && donor) {
            next.artFileId = donor.artFileId;
            next.artFileName = donor.artFileName;
            next.artFileUrl = donor.artFileUrl;
            next.artFileMime = donor.artFileMime;
          }
          return next;
        }),
      };
    });
  };

  // Attach (or replace) the art file for every member sharing this art
  // label. Uploads straight to R2 via the presign/PUT/confirm flow in
  // lib/fileUpload.js; the server never sees the bytes. Passing
  // replacesFileId when swapping an existing attachment creates a new,
  // separate file record that points back at the old one (version chain)
  // rather than mutating it — any PO already built from the old file keeps
  // resolving to exactly those original bytes.
  const attachArt = async (artKey, fileList, replacesFileId) => {
    const file = fileList && fileList[0];
    if (!file) return;
    setUploadingArt(u => ({ ...u, [artKey]: true }));
    try {
      const token = await getToken();
      const uploaded = await uploadFile(file, { kind: 'art', clientId, label: file.name, replacesFileId }, token);
      setArtForGroup(artKey, {
        artFileId: uploaded.id,
        artFileName: uploaded.filename || file.name,
        artFileUrl: uploaded.url || null,
        artFileMime: uploaded.mime_type || file.type || null,
      });
      showToast?.(uploaded.reused ? 'Art already on file — reused existing upload' : 'Art attached');
    } catch (e) {
      showToast?.('Art upload failed: ' + e.message, 'error');
    } finally {
      setUploadingArt(u => ({ ...u, [artKey]: false }));
    }
  };
  const removeArt = (artKey) => {
    setArtForGroup(artKey, { artFileId: null, artFileName: null, artFileUrl: null, artFileMime: null });
  };

  // "Use existing" — only offered once a real client is linked (that's the
  // whole library). Fetches on demand rather than eagerly, since most job
  // edits won't need it.
  const [libraryFor, setLibraryFor] = useState(null); // art key currently showing the picker
  // Per-item art override is collapsed by default (see the item checklist
  // below) — this tracks which items have explicitly expanded it. An item
  // whose art already genuinely differs from the job's shared art shows
  // expanded automatically, without needing to click through.
  const [itemArtOverrideOpen, setItemArtOverrideOpen] = useState({});
  const [libraryFiles, setLibraryFiles] = useState(null);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const openLibrary = async (artKey) => {
    setLibraryFor(artKey);
    if (libraryFiles) return; // already fetched this session
    setLibraryLoading(true);
    try {
      const token = await getToken();
      const { files } = await api.withToken(token).getClientFiles(clientId, 'art');
      setLibraryFiles(files);
    } catch (e) {
      showToast?.('Could not load art library: ' + e.message, 'error');
      setLibraryFiles([]);
    } finally {
      setLibraryLoading(false);
    }
  };
  const pickFromLibrary = (artKey, file) => {
    setArtForGroup(artKey, {
      artFileId: file.id,
      artFileName: file.filename || file.label,
      artFileUrl: file.url || null,
      artFileMime: file.mime_type || null,
    });
    setLibraryFor(null);
  };

  const isManualDraft = !!draft && isManualJob(draft);
  const dm = (draft && !isManualDraft) ? mtx(draft.matrixId) : null;
  const dType = isManualDraft ? manualDecoType(draft) : (dm ? dm.type : null);
  const isEmb = dType === 'embroidery';
  const isHT  = dType === 'heat_transfer';
  // needCount/needSize drive the MATRIX row-selector inputs (which tier row to
  // read). A manual job reads no grid, so it gets its own compact field set
  // below instead.
  const needCount = !!dm && (dm.type==='screen_print' || dm.type==='embroidery');
  const needSize  = !!dm && dm.type==='heat_transfer';
  const dCount = isHT ? (Number(draft?.printWidth)||0) * (Number(draft?.printHeight)||0) : (Number(draft?.count) || 0);
  const combined = draft ? (draft.members||[]).reduce((s,m)=>s+(Number(qtyOf(m.id))||0),0) : 0;
  const locCount = draft ? Math.max(1, (draft.locations||[]).filter(l=>(l||'').trim()).length) : 1;   // # of print locations (same artwork)
  const impressions = combined * locCount;                   // total prints across all locations
  const rawRunRate = (dm && impressions>0 && (!(needCount||needSize) || dCount>0)) ? matrixUnitPrice(dm, impressions, dCount) : null;
  // Same fix as computeJobCharges/the saved-job list: the matrix cell is
  // $/sq-in for heat transfer, not the final per-piece rate.
  const runRate = isManualDraft ? manualRunCostOf(draft) : ((rawRunRate != null && isHT) ? rawRunRate * dCount : rawRunRate);
  const fees = (dm && dm.pricing && dm.pricing.charges) || [];
  const locsFilled = draft ? (draft.locations||[]).some(l=>(l||'').trim()) : false;
  const manualPriced = !isManualDraft || (manualRunCostOf(draft) > 0 || manualSetupCostOf(draft) > 0);
  const canSave = !!draft && !!draft.matrixId && draft.members.length > 0 && locsFilled && manualPriced;

  return (
    <div style={{ marginTop:16 }}>
      <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:8,gap:12 }}>
        <div>
          <div style={{ fontSize:13,fontWeight:600,color:'#0F172A' }}>Decoration jobs</div>
          <div style={{ fontSize:11,color:'#94A3B8' }}>Combine garments under one imprint to hit the combined-qty price break; setup is charged once.</div>
        </div>
        {!draft && <button onClick={newDraft} style={{ background:G,color:'#fff',border:'none',borderRadius:7,padding:'6px 12px',fontSize:12,fontWeight:500,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap' }}>+ New job</button>}
      </div>

      {jobs.map(job => {
        const jobManual = isManualJob(job);
        const m = jobManual ? null : mtx(job.matrixId);
        const jobType = jobManual ? manualDecoType(job) : (m ? m.type : null);
        const mems = jobMembers(job);
        const cq = mems.reduce((s,x)=>s+(Number(qtyOf(x.id))||0),0);
        const isJobHT = jobType==='heat_transfer';
        const jobRowKey = isJobHT ? (Number(job.printWidth)||0)*(Number(job.printHeight)||0) : (Number(job.count)||0);
        const rawRr = (!jobManual && m && cq>0) ? matrixUnitPrice(m, cq, jobRowKey) : null;
        const rr = jobManual ? manualRunCostOf(job) : ((rawRr != null && isJobHT) ? rawRr * jobRowKey : rawRr);
        const arts = [...new Set(mems.map(x=>x.art||'Art'))];
        return (
          <div key={job.id} style={{ border:'1px solid #E2E8F0',borderRadius:8,padding:'10px 12px',marginBottom:8,background:'#F8FAFC' }}>
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8 }}>
              <div style={{ fontSize:12,color:'#0F172A' }}>
                <strong>{jobType ? decoTypeShort(jobType) : 'Job'} · {((Array.isArray(job.locations)&&job.locations.length?job.locations:[job.location]).filter(Boolean).join(' + '))||'Location'}</strong>
                {isJobHT
                  ? (job.printWidth && job.printHeight ? ` · ${job.printWidth}"×${job.printHeight}" (${jobRowKey} sq in)` : '')
                  : (job.count ? ` · ${job.count} ${jobType==='embroidery'?'stitches':'colors'}` : '')}
                {jobManual && (
                  <span style={{ marginLeft:8, background:'#EFF6FF', color:'#1D4ED8', fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:8 }}>
                    Manual cost
                  </span>
                )}
                {job.foldIntoGarment && (
                  <span style={{ marginLeft:8, background:`${G}18`, color:'#065F46', fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:8 }}>
                    Blended price
                  </span>
                )}
                {(job.materialsOnly ?? job.foldIntoGarment) && (
                  <span style={{ marginLeft:6, background:'#FEF3C7', color:'#92400E', fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:8 }}>
                    Supplier PO
                  </span>
                )}
                <div style={{ fontSize:11,color:'#64748B',marginTop:2 }}>
                  {job.foldIntoGarment
                    ? `${mems.length} item${mems.length===1?'':'s'} · combined ${cq} pcs · adds ${rr!=null ? `$${rr.toFixed(2)}/pc` : '—'} to garment cost · no separate charge to client${(job.materialsOnly ?? job.foldIntoGarment) ? ' · supplier PO' : ' · decorator PO'}`
                    : <>{mems.length} item{mems.length===1?'':'s'} · combined {cq} pcs · {rr!=null ? `run $${rr.toFixed(2)}/pc` : 'run unavailable at this qty'} · {arts.length} art{arts.length===1?'':'s'}</>}
                </div>
              </div>
              <div style={{ display:'flex',gap:6,flexShrink:0 }}>
                <button onClick={()=>editJob(job)} style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:6,padding:'3px 9px',fontSize:11,cursor:'pointer',fontFamily:'inherit',color:'#475569' }}>Edit</button>
                <button onClick={()=>removeJob(job.id)} style={{ background:'#fff',border:'1px solid #FECACA',borderRadius:6,padding:'3px 9px',fontSize:11,cursor:'pointer',fontFamily:'inherit',color:'#B91C1C' }}>Remove</button>
              </div>
            </div>
          </div>
        );
      })}

      {draft && (
        <div style={{ border:`1px solid ${G}55`,borderRadius:8,padding:'12px 14px',background:'#F0FDF4' }}>
          <label style={{ display:'flex', alignItems:'flex-start', gap:8, marginBottom:10, padding:'8px 10px',
            background: isManualDraft ? '#EFF6FF' : '#fff', border: `1px solid ${isManualDraft ? '#93C5FD' : '#E2E8F0'}`,
            borderRadius:8, cursor:'pointer' }}>
            <input type="checkbox" checked={isManualDraft} style={{ marginTop:2 }}
              onChange={e=>{
                const checked = e.target.checked;
                setDraft(d=>({ ...d, manual: checked, matrixId: checked ? MANUAL_MATRIX_ID : '',
                  count:'', printWidth:'', printHeight:'', feeIndexes:[] }));
              }}/>
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:'#0F172A' }}>No matrix — enter my cost manually</div>
              <div style={{ fontSize:11, color:'#64748B', marginTop:2 }}>
                For a decorator you haven't built a price grid for yet. Type what they charge you; margin still applies on top.
              </div>
            </div>
          </label>

          {!isManualDraft && (
          <div style={{ display:'grid',gridTemplateColumns: needCount?'2fr 1fr':needSize?'2fr 1fr 1fr':'1fr',gap:8,marginBottom:10 }}>
            <div>
              <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>Matrix</label>
              <select value={draft.matrixId} onChange={e=>setDraft(d=>({...d,matrixId:e.target.value,count:'',printWidth:'',printHeight:'',feeIndexes:[]}))} style={{...inp,width:'100%'}}>
                <option value="">Select…</option>
                {(matrices||[]).map(m=><option key={m.id} value={m.id}>{m.name} · {decoTypeShort(m.type)}{m.vendor_name?` (${m.vendor_name})`:''}</option>)}
              </select>
            </div>
            {needCount && (
              <div>
                <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>{isEmb?'Stitches':'# Colors'}</label>
                <input value={draft.count} onChange={e=>setDraft(d=>({...d,count:e.target.value.replace(/[^0-9]/g,'')}))} placeholder={isEmb?'8000':'3'} inputMode="numeric" style={{...inp,width:'100%'}}/>
              </div>
            )}
            {needSize && (
              <>
                <div>
                  <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>Width (in)</label>
                  <input value={draft.printWidth||''} onChange={e=>setDraft(d=>({...d,printWidth:e.target.value.replace(/[^0-9.]/g,'')}))} placeholder="4" inputMode="decimal" style={{...inp,width:'100%'}}/>
                </div>
                <div>
                  <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>Height (in)</label>
                  <input value={draft.printHeight||''} onChange={e=>setDraft(d=>({...d,printHeight:e.target.value.replace(/[^0-9.]/g,'')}))} placeholder="3" inputMode="decimal" style={{...inp,width:'100%'}}/>
                </div>
              </>
            )}
          </div>
          )}
          {needSize && dCount > 0 && (
            <div style={{ fontSize:10,color:'#64748B',marginTop:-4,marginBottom:10 }}>
              = {dCount.toFixed(2)} sq in — selects the matrix's size band automatically
            </div>
          )}

          {/* Manual pricing — for a distributor who hasn't built matrices yet,
              or a one-off quote the grid doesn't cover. These are COSTS: margin
              is applied on top exactly as it is to matrix pricing. No decorator
              field here on purpose — a manual job has no vendor tie, so its PO
              always comes out with the decorator name/contact blank; add that
              when sending it. */}
          {isManualDraft && (
            <div style={{ border:`1px solid ${G}55`,borderRadius:8,padding:'10px 12px',marginBottom:10,background:'#fff' }}>
              <div style={{ fontSize:11,fontWeight:600,color:'#0F172A' }}>Your decoration cost</div>
              <div style={{ fontSize:10,color:'#94A3B8',marginBottom:8 }}>
                What the decorator charges you. Your margin is applied on top automatically, same as matrix pricing.
              </div>
              <div style={{ display:'grid',gridTemplateColumns:'1.2fr 1fr 1fr',gap:8 }}>
                <div>
                  <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>Method</label>
                  <select value={draft.manualType||'screen_print'} onChange={e=>setDraft(d=>({...d,manualType:e.target.value}))} style={{...inp,width:'100%'}}>
                    <option value="screen_print">Screen Print</option>
                    <option value="embroidery">Embroidery</option>
                    <option value="heat_transfer">Heat Transfer</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>Setup fee (per art)</label>
                  <input value={draft.manualSetupCost ?? ''} inputMode="decimal" placeholder="0.00"
                    onChange={e=>setDraft(d=>({...d,manualSetupCost:e.target.value.replace(/[^0-9.]/g,'')}))}
                    style={{...inp,width:'100%'}}/>
                </div>
                <div>
                  <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>Cost per item</label>
                  <input value={draft.manualRunCost ?? ''} inputMode="decimal" placeholder="1.75"
                    onChange={e=>setDraft(d=>({...d,manualRunCost:e.target.value.replace(/[^0-9.]/g,'')}))}
                    style={{...inp,width:'100%'}}/>
                </div>
              </div>
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1.4fr',gap:8,marginTop:8 }}>
                <div>
                  <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>{isEmb?'Stitches':'# Colors'} <span style={{color:'#94A3B8'}}>(optional)</span></label>
                  <input value={draft.count||''} inputMode="numeric" placeholder={isEmb?'8000':'1'}
                    onChange={e=>setDraft(d=>({...d,count:e.target.value.replace(/[^0-9]/g,'')}))}
                    style={{...inp,width:'100%'}}/>
                </div>
                <div>
                  <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>Decorator <span style={{color:'#94A3B8'}}>(optional)</span></label>
                  <input value={draft.manualVendorName||''} placeholder="Leave blank to fill in on the PO"
                    onChange={e=>setDraft(d=>({...d,manualVendorName:e.target.value}))}
                    style={{...inp,width:'100%'}}/>
                </div>
              </div>
              <div style={{ fontSize:10,color:'#94A3B8',marginTop:6,lineHeight:1.5 }}>
                Setup is charged once per distinct artwork — not per item, and not per location.
                The per-item cost is per print, so two locations cost twice the per-piece run.
                {' '}If the decorator name matches one on your Decorators page, their contact details are
                pulled onto the PO; otherwise it still prints on the PO as typed, with contact info blank.
              </div>
            </div>
          )}

          <div style={{ marginBottom:10 }}>
            <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>
              Print location(s) <span style={{ color:'#94A3B8' }}>· same artwork on each — adds to the run, still one setup</span>
            </label>
            {(draft.locations||['']).map((loc,i)=>(
              <div key={i} style={{ display:'flex',gap:6,marginBottom:5 }}>
                <input value={loc} onChange={e=>setLoc(i,e.target.value)} placeholder={i===0?'Full Front':'e.g. Full Back, Left Chest, Right Sleeve'} style={{...inp,flex:1}}/>
                {(draft.locations||[]).length>1 && (
                  <button onClick={()=>rmLoc(i)} style={{ background:'#fff',border:'1px solid #FECACA',borderRadius:6,padding:'0 9px',fontSize:14,cursor:'pointer',fontFamily:'inherit',color:'#B91C1C' }}>×</button>
                )}
              </div>
            ))}
            <button onClick={addLoc} style={{ background:'none',border:'none',color:G,fontSize:11,fontWeight:500,cursor:'pointer',fontFamily:'inherit',padding:0 }}>+ Add location (same artwork)</button>
          </div>

          {(needCount || needSize || isManualDraft) && (
            <div style={{ marginBottom:10 }}>
              <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>
                {isEmb ? 'Thread colors (optional)' : 'Print colors (optional)'}
              </label>
              <input value={draft.printColors||''} onChange={e=>setDraft(d=>({...d,printColors:e.target.value}))}
                placeholder={isEmb ? 'e.g. White, Navy thread' : isHT ? 'e.g. Full Color Heat Transfer' : 'e.g. PMS 286, White, Black'} style={{...inp,width:'100%'}}/>
              <div style={{ fontSize:10,color:'#94A3B8',marginTop:2 }}>The actual ink/thread colors — printed on the decorator PO.</div>
            </div>
          )}

          {(() => {
            // Job-level art — one artwork for the whole job, the common
            // case. Shown as whatever the first member currently has (if
            // any); attaching/replacing/removing here broadcasts to every
            // item in the job at once, regardless of each item's own art
            // label. Per-item controls below still exist for the rare case
            // where one item in the job genuinely needs different art —
            // that's a deliberate override, not the default flow.
            const first = draft.members[0];
            const jobArt = first && first.artFileId ? first : null;
            const jobUploading = uploadingArt['__job__'];
            return (
              <div style={{ marginBottom:14, padding:'10px 12px', background:'#fff', border:`1px solid ${G}55`, borderRadius:8 }}>
                <div style={{ fontSize:11, fontWeight:600, color:'#374151', marginBottom:6 }}>Artwork for this job</div>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  {jobArt ? (
                    <>
                      {jobArt.artFileMime && jobArt.artFileMime.startsWith('image/') ? (
                        <a href={jobArt.artFileUrl} target="_blank" rel="noreferrer">
                          <img src={jobArt.artFileUrl} alt={jobArt.artFileName || 'Art'}
                            style={{ width:32,height:32,borderRadius:4,objectFit:'contain',border:'1px solid #E2E8F0',background:'#fff',padding:1 }}/>
                        </a>
                      ) : null}
                      <a href={jobArt.artFileUrl} target="_blank" rel="noreferrer" style={{ fontSize:12,color:G,textDecoration:'none' }}>
                        📎 {jobArt.artFileName || 'Attached art'}
                      </a>
                      <label style={{ fontSize:11,color:'#64748B',cursor:'pointer',textDecoration:'underline' }}>
                        Replace
                        <input type="file" accept="image/*,.pdf,.ai,.eps,.svg" style={{ display:'none' }}
                          onChange={e=>attachArt('__job__', e.target.files, jobArt.artFileId)}/>
                      </label>
                      <button onClick={()=>removeArt('__job__')} style={{ background:'none',border:'none',color:'#B91C1C',fontSize:11,cursor:'pointer',fontFamily:'inherit',padding:0 }}>Remove</button>
                    </>
                  ) : (
                    <>
                      <label style={{ fontSize:12,color:G,cursor:'pointer',fontWeight:500 }}>
                        {jobUploading ? 'Uploading…' : '📎 Upload art for this job'}
                        <input type="file" accept="image/*,.pdf,.ai,.eps,.svg" style={{ display:'none' }} disabled={jobUploading}
                          onChange={e=>attachArt('__job__', e.target.files, null)}/>
                      </label>
                      {clientId && (
                        <button onClick={()=>openLibrary('__job__')} style={{ background:'none',border:'none',color:'#475569',fontSize:11,textDecoration:'underline',cursor:'pointer',fontFamily:'inherit',padding:0 }}>
                          Use existing art
                        </button>
                      )}
                    </>
                  )}
                </div>
                {libraryFor === '__job__' && (
                  <div style={{ marginTop:6,border:'1px solid #E2E8F0',borderRadius:6,padding:8,background:'#F8FAFC',maxHeight:160,overflowY:'auto' }}>
                    {libraryLoading ? (
                      <div style={{ fontSize:11,color:'#94A3B8' }}>Loading this client's art…</div>
                    ) : !libraryFiles || libraryFiles.length===0 ? (
                      <div style={{ fontSize:11,color:'#94A3B8' }}>No previous art on file for this client yet.</div>
                    ) : (
                      libraryFiles.map(f => (
                        <div key={f.id} onClick={()=>pickFromLibrary('__job__', f)}
                          style={{ display:'flex',justifyContent:'space-between',padding:'4px 6px',fontSize:11,color:'#334155',cursor:'pointer',borderRadius:4 }}
                          onMouseEnter={e=>e.currentTarget.style.background='#fff'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                          <span>📎 {f.filename || f.label || 'Untitled'}</span>
                          <span style={{ color:'#94A3B8' }}>{new Date(f.created_at).toLocaleDateString()}</span>
                        </div>
                      ))
                    )}
                    <button onClick={()=>setLibraryFor(null)} style={{ marginTop:4,background:'none',border:'none',color:'#94A3B8',fontSize:10,cursor:'pointer',fontFamily:'inherit',padding:0 }}>Close</button>
                  </div>
                )}
              </div>
            );
          })()}

          <div style={{ fontSize:11,fontWeight:600,color:'#374151',marginBottom:6 }}>Items in this job <span style={{ fontWeight:400,color:'#94A3B8' }}>· each can have its own art &amp; ink</span></div>
          {apparelProducts.length===0 ? (
            <div style={{ fontSize:11,color:'#94A3B8',marginBottom:10 }}>No apparel items in this presentation yet.</div>
          ) : (
            <div style={{ display:'flex',flexDirection:'column',gap:6,marginBottom:10 }}>
              {apparelProducts.map(p => {
                const idx = memIndex(p.id);
                const on = idx >= 0;
                const mem = on ? draft.members[idx] : null;
                return (
                  <div key={p.id} style={{ border:`1px solid ${on?G+'55':'#E2E8F0'}`,borderRadius:6,padding:'6px 8px',background:on?'#fff':'transparent' }}>
                    <label style={{ display:'flex',alignItems:'center',gap:8,fontSize:12,color:'#334155',cursor:'pointer' }}>
                      <input type="checkbox" checked={on} onChange={e=>toggleMember(p.id, e.target.checked)}/>
                      {p.itemNo!=null && <span style={{ fontWeight:600,color:'#0F172A' }}>#{p.itemNo}</span>}
                      <span>{p.name}</span>
                      <span style={{ color:'#94A3B8' }}>· {qtyOf(p.id)||0} pcs</span>
                    </label>
                    {on && (
                      <div style={{ paddingLeft:24, marginTop:6 }}>
                        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:6 }}>
                          <input value={mem.art} onChange={e=>setMemberArtLabel(p.id,e.target.value)} placeholder="Art / logo (e.g. Logo A)" style={{...inp,fontSize:11}}/>
                          <input value={mem.ink} onChange={e=>setMember(p.id,'ink',e.target.value)} placeholder={isEmb?'Thread color':'Ink color (e.g. White)'} style={{...inp,fontSize:11}}/>
                        </div>
                        {(() => {
                          const artKey = artKeyOf(mem);
                          const jobArtFileId = draft.members[0]?.artFileId || null;
                          const isGenuineOverride = mem.artFileId && mem.artFileId !== jobArtFileId;
                          const expanded = !!itemArtOverrideOpen[p.id] || isGenuineOverride;
                          if (!expanded) {
                            return (
                              <button onClick={()=>setItemArtOverrideOpen(o=>({...o,[p.id]:true}))}
                                style={{ background:'none',border:'none',color:'#94A3B8',fontSize:11,textDecoration:'underline',cursor:'pointer',fontFamily:'inherit',padding:0,marginTop:6 }}>
                                Use different art for this item
                              </button>
                            );
                          }
                          return (
                        <>
                        <div style={{ display:'flex',alignItems:'center',gap:8,marginTop:6,flexWrap:'wrap' }}>
                          {mem.artFileId ? (
                            <>
                              {mem.artFileMime && mem.artFileMime.startsWith('image/') ? (
                                <a href={mem.artFileUrl} target="_blank" rel="noreferrer">
                                  <img src={mem.artFileUrl} alt={mem.artFileName || 'Art'}
                                    style={{ width:28,height:28,borderRadius:4,objectFit:'contain',border:'1px solid #E2E8F0',background:'#fff',padding:1 }}/>
                                </a>
                              ) : null}
                              <a href={mem.artFileUrl} target="_blank" rel="noreferrer" style={{ fontSize:11,color:G,textDecoration:'none',display:'flex',alignItems:'center',gap:4 }}>
                                📎 {mem.artFileName || 'Attached art'}
                              </a>
                              <span style={{ fontSize:10,color:'#94A3B8' }}>(shared by all "{mem.art || 'Art'}" items)</span>
                              <label style={{ fontSize:11,color:'#64748B',cursor:'pointer',textDecoration:'underline' }}>
                                Replace
                                <input type="file" accept="image/*,.pdf,.ai,.eps,.svg" style={{ display:'none' }}
                                  onChange={e=>attachArt(artKey, e.target.files, mem.artFileId)}/>
                              </label>
                              <button onClick={()=>removeArt(artKey)} style={{ background:'none',border:'none',color:'#B91C1C',fontSize:11,cursor:'pointer',fontFamily:'inherit',padding:0 }}>Remove</button>
                            </>
                          ) : (
                            <>
                              <label style={{ fontSize:11,color:G,cursor:'pointer',fontWeight:500 }}>
                                {uploadingArt[artKey] ? 'Uploading…' : '📎 Upload new'}
                                <input type="file" accept="image/*,.pdf,.ai,.eps,.svg" style={{ display:'none' }} disabled={uploadingArt[artKey]}
                                  onChange={e=>attachArt(artKey, e.target.files, null)}/>
                              </label>
                              {clientId && (
                                <button onClick={()=>openLibrary(artKey)} style={{ background:'none',border:'none',color:'#475569',fontSize:11,textDecoration:'underline',cursor:'pointer',fontFamily:'inherit',padding:0 }}>
                                  Use existing art
                                </button>
                              )}
                            </>
                          )}
                        </div>
                        {libraryFor === artKey && (
                          <div style={{ marginTop:6,border:'1px solid #E2E8F0',borderRadius:6,padding:8,background:'#fff',maxHeight:160,overflowY:'auto' }}>
                            {libraryLoading ? (
                              <div style={{ fontSize:11,color:'#94A3B8' }}>Loading this client's art…</div>
                            ) : !libraryFiles || libraryFiles.length===0 ? (
                              <div style={{ fontSize:11,color:'#94A3B8' }}>No previous art on file for this client yet.</div>
                            ) : (
                              libraryFiles.map(f => (
                                <div key={f.id} onClick={()=>pickFromLibrary(artKey, f)}
                                  style={{ display:'flex',justifyContent:'space-between',padding:'4px 6px',fontSize:11,color:'#334155',cursor:'pointer',borderRadius:4 }}
                                  onMouseEnter={e=>e.currentTarget.style.background='#F8FAFC'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                                  <span>📎 {f.filename || f.label || 'Untitled'}</span>
                                  <span style={{ color:'#94A3B8' }}>{new Date(f.created_at).toLocaleDateString()}</span>
                                </div>
                              ))
                            )}
                            <button onClick={()=>setLibraryFor(null)} style={{ marginTop:4,background:'none',border:'none',color:'#94A3B8',fontSize:10,cursor:'pointer',fontFamily:'inherit',padding:0 }}>Close</button>
                          </div>
                        )}
                        </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <label style={{ display:'flex', alignItems:'flex-start', gap:8, marginBottom:8, padding:'10px 12px',
            background: draft.foldIntoGarment ? `${G}0D` : '#F8FAFC', border: `1px solid ${draft.foldIntoGarment ? G : '#E2E8F0'}`,
            borderRadius:8, cursor:'pointer' }}>
            <input type="checkbox" checked={!!draft.foldIntoGarment} style={{ marginTop:2 }}
              onChange={e=>setDraft(d=>({...d,foldIntoGarment:e.target.checked,
                // Unchecking the blend clears materials-only too — a materials
                // cost is always blended, so the sub-option can't outlive it.
                materialsOnly: e.target.checked ? (d.materialsOnly ?? false) : false}))}/>
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:'#0F172A' }}>Show the client one blended price</div>
              <div style={{ fontSize:11, color:'#64748B', marginTop:2 }}>
                This cost adds into the garment's own cost (margin applies to the combined total) and never
                appears as a separate line on the client's proposal — just one per-piece price.
                The decorator still gets a full PO with the setup and run charges itemised.
              </div>
            </div>
          </label>

          {/* Materials-only is a SUB-option of blending, not an alternative to
              it. Previously one checkbox meant both "blend the price" and "no
              decorator PO", so quoting a blended price for real decoration work
              silently suppressed the decorator's PO. */}
          {draft.foldIntoGarment && (
            <label style={{ display:'flex', alignItems:'flex-start', gap:8, marginBottom:14, marginLeft:18,
              padding:'9px 12px', background: draft.materialsOnly ? '#FFFBEB' : '#F8FAFC',
              border:`1px solid ${draft.materialsOnly ? '#FDE68A' : '#E2E8F0'}`, borderRadius:8, cursor:'pointer' }}>
              <input type="checkbox" checked={!!draft.materialsOnly} style={{ marginTop:2 }}
                onChange={e=>setDraft(d=>({...d,materialsOnly:e.target.checked}))}/>
              <div>
                <div style={{ fontSize:12, fontWeight:600, color:'#0F172A' }}>This vendor supplies materials, not decoration</div>
                <div style={{ fontSize:11, color:'#64748B', marginTop:2 }}>
                  Only for a materials purchase — e.g. heat-transfer stock bought from one vendor that a
                  separate decorator applies. They get a supplier-style PO instead of a decorator PO with
                  garments and screens. Leave unchecked if this vendor actually decorates.
                </div>
              </div>
            </label>
          )}

          {fees.length>0 && (
            <>
              <div style={{ fontSize:11,fontWeight:600,color:'#374151',marginBottom:6 }}>Setup fees (charged once for the job)</div>
              <div style={{ display:'flex',flexWrap:'wrap',gap:12,marginBottom:10 }}>
                {fees.map((f,i)=>(
                  <label key={i} style={{ display:'inline-flex',alignItems:'center',gap:6,fontSize:11,color:'#475569',cursor:'pointer' }}>
                    <input type="checkbox" checked={draft.feeIndexes.includes(i)}
                      onChange={e=>setDraft(d=>({...d,feeIndexes: e.target.checked ? [...d.feeIndexes,i] : d.feeIndexes.filter(x=>x!==i)}))}/>
                    {f.name} <span style={{ color:'#94A3B8' }}>{f.basis==='per_unit'?`$${f.amount}/unit`:(f.basis==='per_color'||f.basis==='per_screen')?`$${f.amount}/color`:`$${f.amount} flat`}</span>
                  </label>
                ))}
              </div>
            </>
          )}

          <div style={{ fontSize:12,color:'#475569',marginBottom:10,padding:'8px 10px',background:'#fff',border:'1px solid #E2E8F0',borderRadius:6 }}>
            Combined: <strong>{combined} pcs</strong>{locCount>1 ? <> · <strong>{impressions} prints</strong> ({locCount} locations)</> : null}
            {runRate!=null ? <> · Run <strong style={{color:G}}>${runRate.toFixed(2)}/print</strong>{locCount>1 ? <> × {locCount} = <strong style={{color:G}}>${(runRate*locCount).toFixed(2)}/pc</strong></> : <> folded into each garment</>}</>
              : (isManualDraft ? <span style={{color:'#B45309'}}> · enter a run cost or a setup above</span>
                               : <span style={{color:'#B45309'}}> · run price unavailable at this qty / color count</span>)}
            {isManualDraft && manualSetupCostOf(draft) > 0 && (
              <> · Setup <strong style={{color:G}}>${manualSetupCostOf(draft).toFixed(2)}</strong> per art</>
            )}
          </div>

          <div style={{ display:'flex',gap:8 }}>
            <button onClick={commit} disabled={!canSave}
              style={{ background:G,color:'#fff',border:'none',borderRadius:7,padding:'6px 14px',fontSize:12,fontWeight:500,cursor:'pointer',fontFamily:'inherit',opacity:canSave?1:0.5 }}>Save job</button>
            <button onClick={()=>setDraft(null)} style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:7,padding:'6px 14px',fontSize:12,cursor:'pointer',fontFamily:'inherit',color:'#475569' }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Presentations({ cartItems, setCart, dist, getToken, showToast, navigate, onDirtyChange, onRegisterSave }) {
  const [name,        setName]        = useState('New Presentation');
  const [clientName,  setClientName]  = useState('');
  const [clientEmail, setClientEmail] = useState('');
  // Linked client from the new ClientPicker (June 2026, Push C). When set,
  // the presentation persists with client_id. When null, falls back to
  // free-text client_name / client_email (state 2 in the picker model). A
  // presentation can also have neither — that's fine.
  const [pickedClient, setPickedClient] = useState(null);
  const [margins,     setMargins]     = useState({});
  const [qtys,        setQtys]        = useState({});
  const [notes,       setNotes]       = useState({});
  const [netOverrides, setNetOverrides] = useState({});
  // Per-presentation pricing-visibility override. Defaults to distributor's
  // global setting; toggle here only affects THIS presentation.
  const [showPricing, setShowPricing] = useState(!!dist?.show_pricing);
  // Mockup images per product id (data URL or remote URL)
  const [mockups, setMockups] = useState({});
  // Generic per-line-item art attachment — the non-apparel counterpart to
  // DecorationJobsPanel's per-member art upload. Any cart line (apparel or
  // not) can have a reference art file attached at quoting time, not just
  // when it happens to be part of a decoration job. Keyed by product id,
  // same shape as a decoration-job member: { fileId, fileName, fileUrl }.
  const [itemArt, setItemArt] = useState({});
  // Per-product charges added by the distributor: { [productId]: [{name,type,supplier_cost,client_price,qty_for_run,_source}] }
  const [productCharges, setProductCharges] = useState({});
  // Extra per-unit COGS folded in from jobs flagged foldIntoGarment — see
  // computeJobCharges. Added into effectiveNet() below so it flows through
  // every existing margin/display/PDF calculation automatically, the same
  // way a manual net override already does.
  const [foldedNetMap, setFoldedNetMap] = useState({});
  // Presentation-level "additional charges" array
  const [additionalCharges, setAdditionalCharges] = useState([]);
  // Combined-decoration jobs (order-level): [{ id, matrixId, location, count, memberIds, feeIndexes }].
  const [decorationJobs, setDecorationJobs] = useState([]);
  // Decoration matrices, loaded once for job pricing.
  const [decoMatrices, setDecoMatrices] = useState([]);
  // Per-product client color override (distributor changing the customer's pick)
  const [colorOverrides, setColorOverrides] = useState({});
  // Per-product display toggles for the client-facing PDF / public viewer
  const [showAllColorsMap, setShowAllColorsMap] = useState({});
  const [showPriceGridMap, setShowPriceGridMap] = useState({});
  // Whether "3rd Party Decoration" is enabled for a product. Gates the decoration
  // matrix panel so it only appears on items you deliberately send to a decorator.
  const [thirdPartyDecoMap, setThirdPartyDecoMap] = useState({});
  // Per-product apparel size grid: { [productId]: [{label,cost,qty}] }. Drives
  // the line's total qty and blended net for _apparel products.
  const [apparelSizesMap, setApparelSizesMap] = useState({});
  // Per-product per-color pricing for apparel: { [productId]: { byColor, colors } }.
  // Lets the size grid re-price when the client color changes.
  const [apparelPricingMap, setApparelPricingMap] = useState({});
  // Per-product, per-tier overrides: { [productId]: { [tierQty]: { margin, price_override } } }
  const [tierOverridesMap, setTierOverridesMap] = useState({});
  // Per-product shipping configuration (June 2026, Push B redo). Shipping is
  // necessarily per-product because each product ships from its own supplier
  // with its own carton data and origin. Multi-supplier presentations can't
  // share a single shipping estimate.
  //   shippingMap: { [productId]: { amount, mode: 'line'|'blended', blendQty, label } | null }
  // A product with no entry (or null) has no shipping configured for it.
  const [shippingMap, setShippingMap] = useState({});
  // True once distributor clicks "+ New presentation" (or arrives from a quote).
  const [startedNew, setStartedNew] = useState(false);
  // ID of the presentation being edited (loaded from saved list). Null = new presentation.
  // When set, Save Draft updates this row instead of creating a new one.
  const [editingId, setEditingId] = useState(null);
  // Track source quote (for auto-flipping it to 'quoted' on save).
  const [fromQuoteId, setFromQuoteId] = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [sending,     setSending]     = useState(false);
  const [pdfLoading,  setPdfLoading]  = useState(false);
  const [pdfStatus,   setPdfStatus]   = useState('');
  const [shareToken,  setShareToken]  = useState(null);
  // Whether to SHOW the share dialog. Previously the modal rendered whenever
  // shareToken was set — but shareToken is also populated when LOADING an
  // existing presentation (so its link stays reusable), which meant opening
  // any previously-shared presentation popped the "Ready to send" dialog
  // unprompted. One piece of state was doing two jobs. The dialog now only
  // appears when the user actually clicks Generate Client Link.
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [fromQuote,   setFromQuote]   = useState(null);
  const [presStatus,  setPresStatus]  = useState('draft');
  const [presChangesMessage, setPresChangesMessage] = useState('');
  const [showCustom,  setShowCustom]  = useState(false);
  const [showApparel, setShowApparel] = useState(false);
  const [showProductSearch, setShowProductSearch] = useState(false);
  // Controls the Create POs modal. Only opens when there's a saved presentation
  // (editingId) — we need the persisted ID so created POs can be linked back.
  const [showPOFlow,  setShowPOFlow]  = useState(false);
  // The persisted presentation id the PO flow reads from. We always save the
  // current state before opening POs so the server builds them from live data.
  const [poPresentationId, setPoPresentationId] = useState(null);
  // Invoice modal: like the PO flow, it needs a SAVED presentation id so the
  // server can validate ownership. We save first, then open it with the
  // freshly-persisted id and prebuilt (already client-priced) line items.
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoicePresentationId, setInvoicePresentationId] = useState(null);
  const [invoiceLineItems, setInvoiceLineItems] = useState([]);

  const cartIds = useMemo(() => new Set(cartItems.map(i => i.id)), [cartItems]);

  // Append a product (catalog or custom) to the cart, dedupe by id.
  const addProductToCart = useCallback((product) => {
    // A product can be added more than once (different colors / prints) — each
    // add is its own cart line with a unique instance id. All per-line state
    // keys by that line id; pricing/inventory use _baseId.
    const line = assignCartLineId(cartItems, product);
    setCart(prev => [...prev, line]);
    if (line._apparel && Array.isArray(line.sizes)) {
      setApparelSizesMap(m => ({ ...m, [line.id]: line.sizes }));
    }
    showToast(`Added ${line.name}`);
    return line;
  }, [cartItems, setCart, showToast]);

  // Add from search. For SanMar/S&S apparel we fetch the live per-size Net cost
  // (blank garment only — decoration is added separately on the presentation/PO)
  // and drop the product into the apparel size grid pre-filled with real sizes +
  // costs. Everything else adds normally.
  const addFromSearch = useCallback(async (product) => {
    const slug = PRICING_SLUG[product?.data_source];
    if (!slug) { addProductToCart(product); return; }
    showToast(`Fetching ${product.supplier_name || 'supplier'} pricing…`);
    try {
      const token = await getToken();
      const data = await api.withToken(token).getSupplierPricing(slug, nativeProductId(product));
      const bySize = data?.ok ? data.bySize : null;
      if (bySize && Object.keys(bySize).length) {
        const sizes = Object.entries(bySize).map(([label, cost]) => ({ label, cost: Number(cost), qty: '' }));
        // size_system !== 'custom' → fixed size labels (supplier defines them).
        const line = addProductToCart({ ...product, _apparel: true, size_system: 'sanmar', sizes });
        if (data.byColor) setApparelPricingMap(m => ({ ...m, [line.id]: { byColor: data.byColor, colors: data.colors || [] } }));
        // Seed the blended-net display to the base (lowest) size cost so the row
        // reads sensibly before any quantities are entered — otherwise it would
        // fall back to the product's (wrong) list net. Recomputes on grid edits.
        const baseCost = Math.min(...sizes.map(s => Number(s.cost) || Infinity));
        if (isFinite(baseCost)) setNetOverrides(o => ({ ...o, [line.id]: baseCost }));
        return;
      }
    } catch (e) {
      console.error('[addFromSearch] pricing fetch failed', e);
    }
    // Non-apparel or pricing unavailable → add as a normal product.
    addProductToCart(product);
  }, [addProductToCart, getToken, showToast]);

  // Apparel size grid changed → store it, and derive the line's total qty and
  // blended (qty-weighted) net cost so the existing totals/PDF math just works.
  const setApparelSizes = useCallback((id, sizes) => {
    setApparelSizesMap(m => ({ ...m, [id]: sizes }));
    const totalQty  = sizes.reduce((s,r)=>s+(Number(r.qty)||0),0);
    const totalCost = sizes.reduce((s,r)=>s+(Number(r.qty)||0)*(Number(r.cost)||0),0);
    setQtys(q => ({ ...q, [id]: totalQty }));
    setNetOverrides(o => ({ ...o, [id]: totalQty > 0 ? +(totalCost/totalQty).toFixed(2) : 0 }));
  }, []);

  // Universal converter: any SanMar/S&S product in the cart that isn't yet an
  // apparel line gets its real per-size Net pricing fetched and dropped into the
  // size grid. This covers every entry path — the builder's search bar AND the
  // search-page card / modal "+ Presentation" button (which add the raw product
  // via the shared cart). The _apparelChecked flag prevents re-processing.
  useEffect(() => {
    const pending = cartItems.filter(p => PRICING_SLUG[p?.data_source] && !p._apparel && !p._apparelChecked);
    if (!pending.length) return;
    let cancelled = false;
    (async () => {
      for (const product of pending) {
        let sizes = null, byColor = null, colors = null;
        try {
          const token = await getToken();
          const slug = PRICING_SLUG[product.data_source];
          const data = await api.withToken(token).getSupplierPricing(slug, nativeProductId(product));
          if (data && data.ok && data.bySize && Object.keys(data.bySize).length) {
            sizes = Object.entries(data.bySize).map(([label, cost]) => ({ label, cost: Number(cost), qty: '' }));
            byColor = data.byColor || null;
            colors = data.colors || null;
          }
        } catch (e) {
          console.error('[apparel convert] pricing fetch failed', e);
        }
        if (cancelled) return;
        if (sizes) {
          setCart(prev => prev.map(p => p.id === product.id
            ? { ...p, _apparel: true, size_system: 'sanmar', sizes, _apparelChecked: true } : p));
          setApparelSizesMap(m => m[product.id] ? m : { ...m, [product.id]: sizes });
          if (byColor) setApparelPricingMap(m => ({ ...m, [product.id]: { byColor, colors: colors || [] } }));
          const baseCost = Math.min(...sizes.map(s => Number(s.cost) || Infinity));
          if (isFinite(baseCost)) setNetOverrides(o => o[product.id] != null ? o : { ...o, [product.id]: baseCost });
        } else {
          // Non-apparel or pricing unavailable → mark checked so we don't retry.
          setCart(prev => prev.map(p => p.id === product.id ? { ...p, _apparelChecked: true } : p));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [cartItems, getToken, setCart]);

  // Reload fix: apparelPricingMap (the per-color cost lookup) only ever gets
  // populated by the converter effect above, which is gated on !p._apparel —
  // but _apparel and _apparelChecked are spread into the saved product JSON
  // (buildItems does {...p}), so they come back as true on every reload.
  // That means the converter effect above permanently skips already-apparel
  // items forever after the first save, and apparelPricingMap stays empty —
  // so changing a garment's color after reopening a saved presentation had
  // no per-color cost to look up and silently didn't reprice.
  //
  // This is a separate effect (not a tweak to the one above) specifically
  // for that gap: re-fetch byColor for apparel items missing it, guarded by
  // a ref (not state) so it's never persisted and can't loop — attempted
  // once per product id per page load, success or failure either way.
  const colorPricingAttempted = useRef(new Set());
  useEffect(() => {
    const pending = cartItems.filter(p =>
      PRICING_SLUG[p?.data_source] && p._apparel &&
      !apparelPricingMap[p.id] && !colorPricingAttempted.current.has(p.id)
    );
    if (!pending.length) return;
    let cancelled = false;
    (async () => {
      for (const product of pending) {
        colorPricingAttempted.current.add(product.id);
        try {
          const token = await getToken();
          const slug = PRICING_SLUG[product.data_source];
          const data = await api.withToken(token).getSupplierPricing(slug, nativeProductId(product));
          if (cancelled) return;
          if (data && data.ok && data.byColor) {
            setApparelPricingMap(m => ({ ...m, [product.id]: { byColor: data.byColor, colors: data.colors || [] } }));
          }
        } catch (e) {
          console.error('[apparel reload] byColor refetch failed', e);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [cartItems, getToken]);

  // Load decoration matrices once (for combined-decoration job pricing).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const rows = await api.withToken(token).listDecorationMatrices();
        if (!cancelled) setDecoMatrices(Array.isArray(rows) ? rows : []);
      } catch { if (!cancelled) setDecoMatrices([]); }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  // Total quantity for a product: apparel size-grid sum, else the plain qty.
  const qtyOf = useCallback((id) => {
    const sizes = apparelSizesMap[id];
    if (Array.isArray(sizes)) return sizes.reduce((s, r) => s + (Number(r.qty) || 0), 0);
    return Number(qtys[id]) || 0;
  }, [apparelSizesMap, qtys]);

  // Recompute job-sourced charges when jobs, member quantities, margins, or
  // matrices change. Job run charges are written into each member's
  // productCharges (folded per-piece at the combined-qty rate); job setups go
  // into order-level additionalCharges (once). Prior job charges are replaced;
  // manually-added charges are preserved.
  useEffect(() => {
    const marginOf = (id) => Number(margins[id]) || 40;
    const { runByProduct, setups, foldedNetByProduct } = computeJobCharges(decorationJobs, decoMatrices, qtyOf, marginOf);
    setProductCharges(prev => {
      const next = {};
      const ids = new Set([...Object.keys(prev), ...Object.keys(runByProduct)]);
      for (const id of ids) {
        const manual = (prev[id] || []).filter(c => !String(c._source || '').startsWith('job|'));
        const merged = [...manual, ...(runByProduct[id] || [])];
        if (merged.length) next[id] = merged;
      }
      return next;
    });
    setAdditionalCharges(prev => {
      const manual = (prev || []).filter(c => !String(c._source || '').startsWith('job|'));
      return [...manual, ...setups];
    });
    setFoldedNetMap(foldedNetByProduct);
  }, [decorationJobs, decoMatrices, qtyOf, margins]);

  // On mount: pull pending quote handoff from sessionStorage and pre-fill the form.
  useEffect(() => {
    let pending = null;
    try {
      const raw = sessionStorage.getItem('pending_presentation');
      if (raw) {
        pending = JSON.parse(raw);
        sessionStorage.removeItem('pending_presentation');
      }
    } catch {}
    if (pending) {
      const company = pending.client_company || pending.client_name || 'Client';
      setName(`${company} — ${todayLabel()}`);
      if (pending.client_name)  setClientName(pending.client_name);
      if (pending.client_email) setClientEmail(pending.client_email);
      setFromQuote(pending);
      // Track source quote so the save flow can auto-flip it to 'quoted'.
      if (pending.from_quote_id) setFromQuoteId(pending.from_quote_id);
      // Arriving from a quote means we ARE building a new presentation —
      // hide the saved-list view automatically.
      setStartedNew(true);
    }
    // NOTE: this used to also do `if (cartItems.length > 0) setStartedNew(true)`,
    // which auto-opened the editor whenever anything was staged — making the
    // saved list unreachable from the sidebar. Staged items are surfaced with
    // a "continue" banner on the list instead, so nothing is lost and the list
    // stays the default landing view.
    const seededQtys = {};
    for (const item of cartItems) {
      if (item.qty) seededQtys[item.id] = item.qty;
    }
    if (Object.keys(seededQtys).length > 0) {
      setQtys(prev => ({ ...seededQtys, ...prev }));
    }
    // eslint-disable-next-line
  }, []);

  const getM = id => margins[id]??dist?.default_margin??40;
  const getQ = id => qtys[id]??null;
  const getNetOverride = id => netOverrides[id]; // undefined if not set

  // Centralized net price calculation: respect distributor's custom override
  // when set, otherwise compute from the supplier list price tier matching
  // the current quantity (walks tiers, finds highest qty break <= currentQty).
  const effectiveNet = (p) => {
    const folded = foldedNetMap[p.id] || 0;
    const ov = getNetOverride(p.id);
    if (typeof ov === 'number' && !isNaN(ov)) return ov + folded;
    const tiers = p.pricing || [];
    if (tiers.length === 0) return folded;
    const currentQty = getQ(p.id) || p.min_qty || 1;
    // Tiers come back qty-ascending; find the deepest break we qualify for.
    let bestTier = tiers[0];
    for (const t of tiers) {
      const breakQty = t.qty || t.q || 0;
      if (currentQty >= breakQty) bestTier = t;
      else break;
    }
    return net(bestTier.list || bestTier.l || 0, bestTier.code || bestTier.c || 'A') + folded;
  };

  const buildItems = useCallback(() => cartItems.map(p => {
    const n = effectiveNet(p);
    const itemCharges = productCharges[p.id] || [];
    const itemQty = getQ(p.id) || p.min_qty || 1;
    const ov = colorOverrides[p.id];
    const effectiveColor = ov !== undefined ? ov : (p.client_color || null);
    const productPayload = { ...p, client_color: effectiveColor };
    const shipCfg = shippingMap[p.id] || null;
    return {
      id: p.id,
      product_id: p.id,
      product: productPayload,
      qty: itemQty,
      margin: getM(p.id),
      sell_price: sell(n, getM(p.id)),
      net_override: typeof getNetOverride(p.id) === 'number' ? getNetOverride(p.id) : null,
      // Folded-in decoration cost (see foldedNetMap / effectiveNet above).
      // Needed separately here because the client-facing apparel size grid
      // (PublicPresentation.jsx's ApparelClientPricing) computes its own
      // per-SIZE margin directly from each size's raw cost — it doesn't
      // read sell_price at all, so without this the folded cost silently
      // never reaches the size grid the client actually sees.
      folded_cost_per_unit: foldedNetMap[p.id] || 0,
      note: notes[p.id] || '',
      mockup_image: mockups[p.id] || null,
      art_file_id: itemArt[p.id]?.fileId || null,
      art_file_name: itemArt[p.id]?.fileName || null,
      art_file_mime: itemArt[p.id]?.fileMime || null,
      show_all_colors: !!showAllColorsMap[p.id],
      show_price_grid: !!showPriceGridMap[p.id],
      third_party_decoration: !!thirdPartyDecoMap[p.id],
      apparel: !!(p._apparel || (apparelSizesMap[p.id] || []).length),
      size_system: (p._apparel || (apparelSizesMap[p.id] || []).length) ? (p.size_system || 'custom') : null,
      sizes: (p._apparel || (apparelSizesMap[p.id] || []).length) ? (apparelSizesMap[p.id] || p.sizes || []) : undefined,
      tier_overrides: tierOverridesMap[p.id] || {},
      charges: itemCharges.map(c => ({
        ...c,
        qty_for_run: c.type === 'Run' ? (c.qty_for_run ?? itemQty) : null,
      })),
      // Per-item shipping (June 2026, Push B). Each product can independently
      // be configured with a shipping_mode ('line' | 'blended') + amount +
      // blend_qty + label. Null when no shipping configured for this product.
      shipping_amount:    shipCfg ? Number(shipCfg.amount)    : null,
      shipping_mode:      shipCfg ? shipCfg.mode              : null,
      shipping_blend_qty: shipCfg ? Number(shipCfg.blendQty)  : null,
      shipping_label:     shipCfg ? (shipCfg.label || null)   : null,
    };
  // eslint-disable-next-line
  }), [cartItems, margins, qtys, notes, netOverrides, mockups, itemArt, productCharges, colorOverrides, showAllColorsMap, showPriceGridMap, tierOverridesMap, shippingMap, thirdPartyDecoMap, apparelSizesMap, foldedNetMap]);

  // Load a saved presentation back into the builder for editing.
  // Reverses the buildItems() serialization: hydrates each line item's product,
  // qty, margin, notes, etc. into the appropriate state slots.
  const loadPresentation = useCallback(async (id) => {
    try {
      const token = await getToken();
      const r = await fetch(`/api/presentations`, { headers: { Authorization: `Bearer ${token}` }});
      if (!r.ok) throw new Error('Failed to fetch');
      const all = await r.json();
      const pres = all.find(p => p.id === id);
      if (!pres) { showToast('Presentation not found', 'error'); return; }

      // Restore form fields
      setName(pres.name || 'New Presentation');
      setClientName(pres.client_name || '');
      setClientEmail(pres.client_email || '');
      setShowPricing(!!pres.show_pricing);
      setAdditionalCharges(Array.isArray(pres.additional_charges) ? pres.additional_charges : []);
      setDecorationJobs(Array.isArray(pres.decoration_jobs) ? pres.decoration_jobs : []);
      // Client decision state — surfaced as a banner below so a rep sees the
      // client's actual feedback right where they're about to make edits,
      // not just a status badge back on the list page.
      setPresStatus(pres.status || 'draft');
      setPresChangesMessage(pres.changes_message || '');

      // Linked client (June 2026, Push C). If client_id is set, fetch the
      // current client record so the ClientPicker shows it as a pill.
      if (pres.client_id) {
        try {
          const token2 = await getToken();
          const res = await api.withToken(token2).getClient(pres.client_id);
          if (res?.client) {
            setPickedClient({ id: res.client.id, name: res.client.name, email: res.client.email });
            // Prefer current client.name over the snapshot stored on the row.
            // Single source of truth — renaming a client updates everywhere.
            if (res.client.name)  setClientName(res.client.name);
            if (res.client.email) setClientEmail(res.client.email);
          }
        } catch { /* fall back to legacy client_name/email already set above */ }
      } else {
        setPickedClient(null);
      }

      // Restore each line item — products live in pres.products as serialized cart items.
      const items = Array.isArray(pres.products) ? pres.products : [];
      const newCart = [];
      const newQtys = {};
      const newMargins = {};
      const newNotes = {};
      const newNetOverrides = {};
      const newMockups = {};
      const newItemArt = {};
      const newCharges = {};
      const newColorOverrides = {};
      const newShowAllColors = {};
      const newShowPriceGrid = {};
      const newThirdPartyDeco = {};
      const newApparelSizes = {};
      const newTierOverrides = {};
      const newShipping = {};

      for (const item of items) {
        const product = item.product || {};
        const id = product.id || item.id || item.product_id;
        if (!id) continue;
        // Product object goes into the cart (search/builder reads from cartItems)
        newCart.push(product);
        if (item.qty != null) newQtys[id] = item.qty;
        if (item.margin != null) newMargins[id] = item.margin;
        if (item.note) newNotes[id] = item.note;
        if (typeof item.net_override === 'number') newNetOverrides[id] = item.net_override;
        if (item.mockup_image) newMockups[id] = item.mockup_image;
        if (item.art_file_id) newItemArt[id] = { fileId: item.art_file_id, fileName: item.art_file_name || null, fileMime: item.art_file_mime || null, fileUrl: null };
        if (Array.isArray(item.charges)) newCharges[id] = item.charges;
        if (product.client_color) newColorOverrides[id] = product.client_color;
        if (item.show_all_colors) newShowAllColors[id] = true;
        if (item.show_price_grid) newShowPriceGrid[id] = true;
        if (item.third_party_decoration) newThirdPartyDeco[id] = true;
        if (item.apparel && Array.isArray(item.sizes)) newApparelSizes[id] = item.sizes;
        if (item.tier_overrides) newTierOverrides[id] = item.tier_overrides;
        // Per-item shipping (June 2026, Push B). Only hydrate when mode is set,
        // since amount/blendQty/label only make sense alongside a mode.
        if (item.shipping_mode === 'line' || item.shipping_mode === 'blended') {
          newShipping[id] = {
            amount:   Number(item.shipping_amount) || 0,
            mode:     item.shipping_mode,
            blendQty: item.shipping_blend_qty || null,
            label:    item.shipping_label || '',
          };
        }
      }

      setCart(newCart);
      setQtys(newQtys);
      setMargins(newMargins);
      setNotes(newNotes);
      setNetOverrides(newNetOverrides);
      setMockups(newMockups);
      setItemArt(newItemArt);
      // Resolve fresh URLs for any restored art — never trust a URL loaded
      // from the saved JSON (presigned URLs expire; the file id is what's
      // durable). Fire-and-forget, small batch, doesn't block the rest of
      // the load.
      const artIds = Object.entries(newItemArt);
      if (artIds.length) {
        (async () => {
          try {
            const t = await getToken();
            const client = api.withToken(t);
            const resolved = await Promise.all(artIds.map(async ([id, a]) => {
              try { const f = await client.getFile(a.fileId); return [id, { ...a, fileUrl: f.url, fileMime: a.fileMime || f.mime_type || null }]; }
              catch { return [id, a]; }
            }));
            setItemArt(prev => ({ ...prev, ...Object.fromEntries(resolved) }));
          } catch { /* non-fatal — art links just won't be clickable until next save/reload */ }
        })();
      }
      setProductCharges(newCharges);
      setColorOverrides(newColorOverrides);
      setShowAllColorsMap(newShowAllColors);
      setShowPriceGridMap(newShowPriceGrid);
      setThirdPartyDecoMap(newThirdPartyDeco);
      setApparelSizesMap(newApparelSizes);
      setTierOverridesMap(newTierOverrides);
      setShippingMap(newShipping);
      setEditingId(id);
      setFromQuote(null); // editing mode hides the "from quote" banner; that was for new builds
      setFromQuoteId(pres.from_quote_id || null);
      setStartedNew(true);
      setShareToken(pres.share_token || null); // makes existing share link reusable
      showToast(`Loaded "${pres.name}" for editing`);
    } catch (e) {
      showToast('Failed to load: ' + e.message, 'error');
    }
  // eslint-disable-next-line
  }, [getToken, showToast]);

  // On mount: pull a "open this specific presentation" handoff left by
  // ClientDetailPage's activity list (which, unlike the PO tab, has no
  // per-id route of its own — Presentations only ever opens a saved row via
  // in-page state, not a URL). Mirrors the pending_presentation quote
  // handoff above, but for an existing id instead of prefilling a new one.
  useEffect(() => {
    let openId = null;
    try {
      const raw = sessionStorage.getItem('pending_presentation_open');
      if (raw) {
        openId = JSON.parse(raw)?.id || null;
        sessionStorage.removeItem('pending_presentation_open');
      }
    } catch {}
    if (openId) loadPresentation(openId);
  // eslint-disable-next-line
  }, []);

  // Save flow (June 2026 refactor): when editing an existing presentation,
  // hitting Save Draft used to silently update the row. Confusing if the user
  // wanted to duplicate. Now: editing → modal with Update/Save-as-new choice;
  // new presentations → direct save (no modal). The modal's default focus
  // is "Update" because that's the common case.
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const onSaveClick = () => {
    if (editingId) { setSaveModalOpen(true); }
    else { doSave({ asNew: false }); }
  };
  const doSave = async ({ asNew }) => {
    setSaveModalOpen(false);
    setSaving(true);
    try {
      const token = await getToken();
      const payload = {
        name, client_name: clientName, client_email: clientEmail, client_id: pickedClient?.id || null,
        products: buildItems(), show_pricing: showPricing,
        additional_charges: additionalCharges,
        decoration_jobs: decorationJobs,
        from_quote_id: fromQuoteId,
      };
      if (editingId && !asNew) {
        // Update path — preserves the existing row + share_token.
        const r = await fetch(`/api/presentations/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type':'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        if (!r.ok) throw new Error('Server error');
        showToast('Presentation updated');
      } else {
        // Create path — used for brand-new presentations AND for the
        // "Save as new" branch when the user is duplicating an existing one.
        const result = await api.withToken(token).createPresentation(payload);
        if (result?.id) setEditingId(result.id);
        if (result?.share_token) setShareToken(result.share_token);
        showToast(asNew ? 'Saved as new presentation' : 'Presentation saved');
      }
    } catch(e) { showToast('Save failed: '+e.message,'error'); }
    finally { setSaving(false); }
  };
  // Legacy alias kept so handleSend/etc. don't break.
  const handleSave = onSaveClick;

  // Persist the current presentation (update if saved, else create) and return
  // the id. Shared by the Create-POs flow so POs always build from live data.
  const persistPresentation = async () => {
    const token = await getToken();
    const payload = {
      name, client_name: clientName, client_email: clientEmail, client_id: pickedClient?.id || null,
      products: buildItems(), show_pricing: showPricing,
      additional_charges: additionalCharges,
      decoration_jobs: decorationJobs,
      from_quote_id: fromQuoteId,
    };
    if (editingId) {
      const r = await fetch(`/api/presentations/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type':'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error('Server error');
      return editingId;
    }
    const result = await api.withToken(token).createPresentation(payload);
    if (result?.id) setEditingId(result.id);
    if (result?.share_token) setShareToken(result.share_token);
    return result?.id;
  };

  // Report to App.jsx whether leaving right now would lose work, so the
  // shell can guard Sidebar/Topbar navigation and beforeunload. Matches the
  // same "cartItems.length > 0 while editing" signal the existing "Back to
  // all presentations" confirm already uses — not a true dirty diff against
  // the last save, so opening an existing presentation with zero edits still
  // reads as "dirty." Consistent with existing behavior; a real diff would
  // need a saved-state snapshot this page doesn't currently keep.
  const isDirtyForLeaveGuard = startedNew && cartItems.length > 0;
  useEffect(() => { onDirtyChange?.(isDirtyForLeaveGuard); }, [isDirtyForLeaveGuard, onDirtyChange]);
  // Clears the flag on unmount as a backstop — normally already false by the
  // time this unmounts, since the guard above only allows navigation away
  // after Save or Discard has run.
  useEffect(() => () => { onDirtyChange?.(false); }, [onDirtyChange]);
  useEffect(() => { onRegisterSave?.(persistPresentation); });

  // Save first, then open the PO flow against the freshly-saved id — so edits
  // made since the last save (art/ink, new jobs, quantities) are reflected.
  const handleCreatePOs = async () => {
    setSaving(true);
    try {
      const id = await persistPresentation();
      if (!id) throw new Error('Could not save presentation');
      setPoPresentationId(id);
      setShowPOFlow(true);
    } catch (e) {
      showToast('Save failed: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Convert the current presentation into an invoice. Same discipline as POs:
  // persist first, then ask the SERVER to price the saved presentation. The
  // server uses the same pricing module as the client's quote page and PDF
  // (server/lib/presentationPricing.js), so the invoice matches exactly what
  // the client was quoted: one line per product (per size for apparel), every
  // decoration/setup charge, shipping, and additional charges by their
  // client-facing labels. The rep can still edit lines in the modal.
  const handleCreateInvoice = async () => {
    setSaving(true);
    try {
      const id = await persistPresentation();
      if (!id) throw new Error('Could not save presentation');

      const token = await getToken();
      const r = await fetch(`/api/presentations/${id}/invoice-lines`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Could not price this presentation');
      const priced = await r.json();
      const items = Array.isArray(priced?.lines) ? priced.lines : [];

      if (items.length === 0) {
        showToast('Add at least one item before creating an invoice', 'error');
        return;
      }

      setInvoicePresentationId(id);
      setInvoiceLineItems(items);
      setShowInvoiceModal(true);
    } catch (e) {
      showToast('Save failed: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const token = await getToken();
      const payload = {
        name, client_name: clientName, client_email: clientEmail, client_id: pickedClient?.id || null,
        products: buildItems(), show_pricing: showPricing,
        additional_charges: additionalCharges,
        decoration_jobs: decorationJobs,
        from_quote_id: fromQuoteId,
      };
      let pres;
      if (editingId) {
        const r = await fetch(`/api/presentations/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type':'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        if (!r.ok) throw new Error('Server error');
        pres = await r.json();
      } else {
        pres = await api.withToken(token).createPresentation(payload);
        if (pres?.id) setEditingId(pres.id);
      }
      setShareToken(pres.share_token);
      setShareModalOpen(true);   // explicit user action — this is the one place the dialog opens
    } catch(e) { showToast('Error: '+e.message,'error'); }
    finally { setSending(false); }
  };

  const handlePDF = async () => {
    setPdfLoading(true);
    try {
      await downloadPDF({ name,client_name:clientName,products:buildItems(),show_pricing:showPricing,additional_charges:additionalCharges }, dist, getToken, msg=>setPdfStatus(msg));
      showToast('PDF downloaded');
    } catch(e) { showToast('PDF error: '+e.message,'error'); }
    finally { setPdfLoading(false); setPdfStatus(''); }
  };

  const rows = cartItems.map(p => {
    const n = effectiveNet(p);
    const m = getM(p.id);
    const q = getQ(p.id) || p.min_qty || 1;
    const itemCharges = productCharges[p.id] || [];
    const chargesNetCost = itemCharges.reduce((sum,c) => {
      const mult = c.type === 'Run' ? (Number(c.qty_for_run ?? q)||1) : 1;
      return sum + (Number(c.supplier_cost)||0) * mult;
    }, 0);
    const chargesClientPrice = itemCharges.reduce((sum,c) => {
      const mult = c.type === 'Run' ? (Number(c.qty_for_run ?? q)||1) : 1;
      return sum + (Number(c.client_price)||0) * mult;
    }, 0);
    return { n, s: sell(n,m), q, chargesNet: chargesNetCost, chargesClient: chargesClientPrice };
  });
  const totalNet = rows.reduce((a,r) => a + r.n*r.q + r.chargesNet, 0)
                 + additionalCharges.reduce((a,c) => a + (Number(c.client_price)||0), 0); // additional charges treated as cost too
  const totalSell = rows.reduce((a,r) => a + r.s*r.q + r.chargesClient, 0)
                  + additionalCharges.reduce((a,c) => a + (Number(c.client_price)||0), 0);
  const blended = totalSell ? Math.round((totalSell-totalNet)/totalSell*100) : 0;

  const isEmpty = !cartItems.length;
  // Show the saved-presentations list at the top of the page when:
  // - the cart is empty (no presentation being built right now), AND
  // - the user hasn't clicked "+ New presentation" yet.
  // Once they click + New, or arrive from a quote, we hide the list and show the builder.
  // Which view: the saved-presentations list, or the editor.
  //
  // This used to be `isEmpty && !startedNew` — i.e. the list only appeared if
  // the cart was EMPTY. That meant clicking "Presentations" in the sidebar
  // with anything staged dropped you straight into the editor, with no way to
  // reach your saved presentations except emptying the cart. The cart having
  // items says nothing about whether you wanted the list.
  //
  // Now it's driven purely by intent: startedNew is set when you click
  // "New presentation", open a saved one, or arrive from a quote — and
  // cleared by "Back to all presentations".
  const showSavedList = !startedNew;

  if (showSavedList) {
    return (
      <div style={{ padding:24 }}>
        {/* Staged items live in the cart until saved. Now that the list is the
            default landing view, they'd otherwise be invisible — this is the
            way back into the in-progress build. */}
        {!isEmpty && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
            gap:16, marginBottom:18, padding:'14px 18px', borderRadius:10,
            background:`${G}0F`, border:`1px solid ${G}33` }}>
            <div style={{ fontSize:13, color:'#0F172A' }}>
              You have <strong>{cartItems.length} item{cartItems.length === 1 ? '' : 's'}</strong> staged in an unsaved presentation.
            </div>
            <button onClick={() => setStartedNew(true)}
              style={{ background:G, border:'none', borderRadius:8, padding:'8px 16px',
                color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer',
                fontFamily:'inherit', whiteSpace:'nowrap' }}>
              Continue building →
            </button>
          </div>
        )}
        <SavedPresentationsList
          getToken={getToken}
          showToast={showToast}
          onStartNew={()=>setStartedNew(true)}
          onEdit={(id) => loadPresentation(id)}/>
      </div>
    );
  }

  return (
    <div style={{ padding:24 }}>
      {/* Always-visible back link + edit-mode banner */}
      <div style={{ marginBottom:14,display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,flexWrap:'wrap' }}>
        <button onClick={()=>{
            if (cartItems.length > 0) {
              if (!window.confirm('Leave this presentation? Any unsaved changes will be lost.\n\nClick OK to leave, or Cancel to keep editing.')) return;
              setCart([]);
              setMargins({}); setQtys({}); setNotes({}); setNetOverrides({});
              setMockups({}); setProductCharges({}); setColorOverrides({});
              setShowAllColorsMap({}); setShowPriceGridMap({}); setTierOverridesMap({});
              setAdditionalCharges([]); setDecorationJobs([]); setName('New Presentation'); setClientName(''); setClientEmail('');
              setPickedClient(null);
              setShippingMap({});
              setFromQuote(null); setFromQuoteId(null); setEditingId(null);
              setPresStatus('draft'); setPresChangesMessage('');
            }
            setStartedNew(false);
          }}
          style={{ background:'none',border:'none',color:'#64748B',fontSize:12,cursor:'pointer',
            fontFamily:'inherit',padding:0 }}>
          ← Back to all presentations
        </button>
        {editingId && (
          <span style={{ fontSize:11,color:'#1E40AF',background:'#DBEAFE',
            padding:'3px 10px',borderRadius:10,fontWeight:600 }}>
            ✏ Editing existing presentation
          </span>
        )}
      </div>
      {presStatus === 'changes_requested' && (
        <div style={{ background:'#FFFBEB',border:'1px solid #FCD34D',borderRadius:12,padding:16,marginBottom:16,
          display:'flex',gap:12,alignItems:'flex-start' }}>
          <div style={{ fontSize:20,lineHeight:1 }}>✎</div>
          <div style={{ flex:1,minWidth:0 }}>
            <div style={{ fontSize:11,color:'#92400E',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:4 }}>
              Client requested changes
            </div>
            {presChangesMessage ? (
              <div style={{ fontSize:13,color:'#78350F',fontStyle:'italic',lineHeight:1.5 }}>
                "{presChangesMessage}"
              </div>
            ) : (
              <div style={{ fontSize:12,color:'#92400E' }}>No specific message was left — you may want to follow up directly.</div>
            )}
          </div>
        </div>
      )}
      {presStatus === 'approved' && (
        <div style={{ background:`${G}0D`,border:`1px solid ${G}55`,borderRadius:12,padding:'10px 16px',marginBottom:16,
          fontSize:13,color:'#065F46',fontWeight:500 }}>
          ✓ This presentation was approved by the client.
        </div>
      )}
      {fromQuote && (
        <div style={{ background:`${G}08`,border:`1px solid ${G}33`,borderRadius:12,padding:16,marginBottom:16,
          display:'flex',gap:14,alignItems:'flex-start' }}>
          {fromQuote.client_logo && (
            <img src={fromQuote.client_logo} alt="Client logo"
              style={{ width:54,height:54,borderRadius:8,objectFit:'contain',background:'#fff',
                border:'1px solid #E2E8F0',padding:6,flexShrink:0 }}/>
          )}
          <div style={{ flex:1,minWidth:0 }}>
            <div style={{ fontSize:11,color:'#059669',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:4 }}>
              Building from quote request
            </div>
            <div style={{ fontSize:14,fontWeight:600,color:'#0F172A',marginBottom:4 }}>
              {fromQuote.client_company || fromQuote.client_name}
            </div>
            <div style={{ fontSize:12,color:'#64748B',marginBottom:fromQuote.notes?8:0 }}>
              {fromQuote.client_name}
              {fromQuote.client_email && <> · {fromQuote.client_email}</>}
              {fromQuote.client_phone && <> · {fromQuote.client_phone}</>}
            </div>
            {fromQuote.notes && fromQuote.notes.trim() && (
              <div style={{ fontSize:12,color:'#475569',background:'#fff',borderLeft:`3px solid ${G}`,
                borderRadius:'0 7px 7px 0',padding:'8px 12px',fontStyle:'italic',lineHeight:1.5,marginTop:4 }}>
                "{fromQuote.notes}"
              </div>
            )}
          </div>
          <button onClick={()=>setFromQuote(null)} aria-label="Hide context"
            style={{ background:'none',border:'none',cursor:'pointer',fontSize:18,color:'#94A3B8',padding:'0 4px',fontFamily:'inherit',flexShrink:0 }}>×</button>
        </div>
      )}
      <div style={{ display:'grid',gridTemplateColumns:'1fr 2fr',gap:12,marginBottom:20 }}>
        <div>
          <div style={{ fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Presentation name</div>
          <input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. ABC Corp — Summer 2025"
            style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:8,padding:'8px 12px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A' }}/>
        </div>
        <div>
          <div style={{ fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Client (optional)</div>
          <ClientPicker
            value={pickedClient}
            fallbackName={clientName}
            fallbackEmail={clientEmail}
            getToken={getToken}
            onPick={(c) => {
              setPickedClient(c);
              if (c) { setClientName(c.name || ''); setClientEmail(c.email || ''); }
            }}
            onClear={() => { setClientName(''); setClientEmail(''); }}
            onChangeFreetext={({ name: nm, email: em }) => {
              if (nm !== undefined) setClientName(nm);
              if (em !== undefined) setClientEmail(em);
            }}/>
        </div>
      </div>
      <div style={{ display:'grid',gridTemplateColumns:'1fr 300px',gap:20 }}>
        <div>
          <AddProductBar
            onOpenSearch={()=>setShowProductSearch(true)}
            onAddCustomClick={()=>setShowCustom(true)}
            onAddApparelClick={()=>setShowApparel(true)}/>
          {isEmpty && (
            <div style={{ background:'#fff',border:'1px dashed #E2E8F0',borderRadius:10,padding:'40px 24px',textAlign:'center' }}>
              <div style={{ fontSize:36,marginBottom:10,opacity:0.5 }}>📋</div>
              <div style={{ fontSize:14,fontWeight:500,color:'#374151',marginBottom:4 }}>No products yet</div>
              <div style={{ fontSize:12,color:'#94A3B8' }}>
                Search above to add a catalog product, or <button onClick={()=>setShowCustom(true)}
                  style={{ background:'none',border:'none',color:G,fontFamily:'inherit',fontSize:12,cursor:'pointer',padding:0,textDecoration:'underline' }}>
                  add a custom item
                </button>.
              </div>
            </div>
          )}
          {cartItems.map((p,idx)=>(
            <ProductRow key={p.id} itemNo={idx+1} product={p} qty={getQ(p.id)||p.min_qty||1} margin={getM(p.id)} note={notes[p.id]||''}
              netOverride={getNetOverride(p.id)}
              mockup={mockups[p.id] || null}
              art={itemArt[p.id] || null}
              clientId={pickedClient?.id || null}
              showToast={showToast}
              charges={productCharges[p.id] || []}
              shipping={shippingMap[p.id] || null}
              onSetShipping={(cfg) => setShippingMap(m => {
                const next = { ...m };
                if (cfg) next[p.id] = cfg;
                else delete next[p.id];
                return next;
              })}
              onMargin={v=>setMargins(m=>({...m,[p.id]:v}))} onQty={v=>setQtys(q=>({...q,[p.id]:v}))}
              onNote={v=>setNotes(n=>({...n,[p.id]:v}))}
              onSetNetOverride={v=>setNetOverrides(o=>{
                const next={...o};
                if (v===null||v===undefined) delete next[p.id];
                else next[p.id]=v;
                return next;
              })}
              onUploadMockup={(dataUrl)=>setMockups(m=>({...m,[p.id]:dataUrl}))}
              onRemoveMockup={()=>setMockups(m=>{ const n={...m}; delete n[p.id]; return n; })}
              onSetArt={(next)=>setItemArt(a=>{
                const n={...a};
                if (next) n[p.id]=next;
                else delete n[p.id];
                return n;
              })}
              onAddCharge={(charge)=>setProductCharges(pc=>({
                ...pc,
                [p.id]: [...(pc[p.id]||[]), charge],
              }))}
              onChangeChargePrice={(idx, val)=>setProductCharges(pc=>({
                ...pc,
                [p.id]: (pc[p.id]||[]).map((c,i)=> i===idx ? {...c, client_price: val} : c),
              }))}
              onChangeChargeRunQty={(idx, val)=>setProductCharges(pc=>({
                ...pc,
                [p.id]: (pc[p.id]||[]).map((c,i)=> i===idx ? {...c, qty_for_run: val} : c),
              }))}
              onRemoveCharge={(idx)=>setProductCharges(pc=>({
                ...pc,
                [p.id]: (pc[p.id]||[]).filter((_,i)=>i!==idx),
              }))}
              onOverrideRunCost={(jobId, newCost)=>setDecorationJobs(jobs=>jobs.map(j=>
                j.id===jobId ? { ...j, runCostOverride: newCost } : j
              ))}
              colorOverride={colorOverrides[p.id]}
              onChangeClientColor={(c)=>{
                setColorOverrides(co=>({ ...co, [p.id]: c }));
                // Re-price the apparel grid for the newly selected color (S&S /
                // SanMar cost varies by color — white cheaper than colors/heathers).
                const byColor = apparelPricingMap[p.id]?.byColor;
                if (byColor && c?.name && byColor[c.name]) {
                  setApparelSizes(p.id, sizesFromPriceMap(byColor[c.name], apparelSizesMap[p.id]));
                }
              }}
              showAllColors={!!showAllColorsMap[p.id]}
              showPriceGrid={!!showPriceGridMap[p.id]}
              thirdPartyDeco={!!thirdPartyDecoMap[p.id]}
              apparelSizes={apparelSizesMap[p.id] || p.sizes || []}
              onSetApparelSizes={(sizes)=>setApparelSizes(p.id, sizes)}
              onToggleShowAllColors={(v)=>setShowAllColorsMap(m=>({ ...m, [p.id]: v }))}
              onToggleShowPriceGrid={(v)=>setShowPriceGridMap(m=>({ ...m, [p.id]: v }))}
              onToggleThirdPartyDeco={(v)=>setThirdPartyDecoMap(m=>({ ...m, [p.id]: v }))}
              tierOverrides={tierOverridesMap[p.id]}
              onSetTierMargin={(qty, m)=>setTierOverridesMap(map=>{
                const prod = { ...(map[p.id] || {}) };
                prod[qty] = { ...(prod[qty] || {}), margin: m };
                return { ...map, [p.id]: prod };
              })}
              onSetTierPriceOverride={(qty, val)=>setTierOverridesMap(map=>{
                const prod = { ...(map[p.id] || {}) };
                if (val === null || val === undefined) {
                  // Drop just the price override; keep the margin override if any.
                  const { price_override, ...rest } = prod[qty] || {};
                  if (Object.keys(rest).length) prod[qty] = rest;
                  else delete prod[qty];
                } else {
                  prod[qty] = { ...(prod[qty] || {}), price_override: val };
                }
                return { ...map, [p.id]: prod };
              })}
              onResetAllTiers={()=>setTierOverridesMap(map=>{
                const next = { ...map };
                delete next[p.id];
                return next;
              })}
              onRemove={()=>setCart(i=>i.filter(x=>x.id!==p.id))}
              getToken={getToken}
              folded={foldedNetMap[p.id] || 0}/>
          ))}
          {!isEmpty && (
            <DecorationJobsPanel
              jobs={decorationJobs}
              matrices={decoMatrices}
              apparelProducts={cartItems
                .map((p, idx) => ({ p, itemNo: idx + 1 }))
                .filter(({p}) => p._apparel || (apparelSizesMap[p.id]||[]).length)
                .map(({p, itemNo}) => ({ id: p.id, itemNo, name: p.name + (colorOverrides[p.id]?.name ? ` · ${colorOverrides[p.id].name}` : '') }))}
              qtyOf={qtyOf}
              onChange={setDecorationJobs}
              getToken={getToken}
              showToast={showToast}
              clientId={pickedClient?.id || null}/>
          )}
          {!isEmpty && (
            <AdditionalChargesSection charges={additionalCharges}
              onAdd={()=>setAdditionalCharges(arr => [...arr, { name:'', client_price: 0 }])}
              onChange={(i, field, val)=>setAdditionalCharges(arr => arr.map((c,idx) => idx===i ? {...c, [field]: val} : c))}
              onRemove={(i)=>setAdditionalCharges(arr => arr.filter((_,idx)=>idx!==i))}/>
          )}
        </div>
        <div style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:12,padding:20,height:'fit-content',position:'sticky',top:16 }}>
          {/* Inline share link box — June 2026. Appears when a presentation
              has been generated and has a share_token. Lets the distributor
              grab the client-facing URL without having to leave the build view
              and dig through the saved presentations list. Stays in sync with
              the editingId: switching between saved presentations swaps the
              token automatically. */}
          {shareToken && (
            <InlineShareLink token={shareToken} />
          )}
          {!shareToken && editingId && (
            <div style={{ fontSize:11,color:'#94A3B8',marginBottom:14,padding:'8px 10px',background:'#F8FAFC',borderRadius:8,border:'1px dashed #E2E8F0' }}>
              Click <strong style={{ color:'#475569',fontWeight:600 }}>Generate Client Link</strong> below to create a shareable URL.
            </div>
          )}
          <div style={{ fontSize:14,fontWeight:600,color:'#0F172A',marginBottom:14 }}>Summary</div>
          {/* Per-product subtotal + any per-product charges nested under it */}
          {cartItems.map(p=>{
            const n2 = effectiveNet(p);
            const itemQty = getQ(p.id)||p.min_qty||1;
            const itemSell = sell(n2,getM(p.id));
            const itemSubtotal = itemSell * itemQty;
            const itemCharges = productCharges[p.id] || [];
            return (
              <div key={p.id} style={{ marginBottom:8,paddingBottom:8,borderBottom:'1px solid #F1F5F9' }}>
                <div style={{ display:'flex',justifyContent:'space-between',fontSize:12,color:'#64748B' }}>
                  <span style={{ flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',marginRight:8 }}>{p.name}</span>
                  <span style={{ color:'#0F172A',fontWeight:500,whiteSpace:'nowrap' }}>
                    {$$(itemSell)} ×{itemQty}
                  </span>
                </div>
                {itemCharges.length > 0 && itemCharges.map((c,i) => {
                  const mult = c.type === 'Run' ? (Number(c.qty_for_run ?? itemQty)||1) : 1;
                  const lineTotal = (Number(c.client_price)||0) * mult;
                  return (
                    <div key={i} style={{ display:'flex',justifyContent:'space-between',fontSize:11,color:'#94A3B8',marginTop:4,paddingLeft:10 }}>
                      <span style={{ flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',marginRight:8 }}>
                        ↳ {c.name}{c.type==='Run' && c.qty_for_run ? ` (×${c.qty_for_run})` : ''}
                      </span>
                      <span style={{ whiteSpace:'nowrap' }}>{$$(lineTotal)}</span>
                    </div>
                  );
                })}
                <div style={{ display:'flex',justifyContent:'space-between',fontSize:11,color:'#94A3B8',marginTop:4 }}>
                  <span></span>
                  <span style={{ color:G,fontWeight:600 }}>{$$(itemSubtotal + itemCharges.reduce((s,c) => {
                    const mult = c.type === 'Run' ? (Number(c.qty_for_run ?? itemQty)||1) : 1;
                    return s + (Number(c.client_price)||0) * mult;
                  }, 0))}</span>
                </div>
              </div>
            );
          })}

          {/* Additional charges (presentation-level: shipping, rush, etc.) */}
          {additionalCharges.length > 0 && (
            <div style={{ marginBottom:8,paddingBottom:8,borderBottom:'1px solid #F1F5F9' }}>
              <div style={{ fontSize:10,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:4 }}>
                Additional charges
              </div>
              {additionalCharges.map((c,i)=>(
                <div key={i} style={{ display:'flex',justifyContent:'space-between',fontSize:12,color:'#64748B',marginBottom:3 }}>
                  <span style={{ flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',marginRight:8 }}>
                    {c.name || 'Charge'}
                  </span>
                  <span style={{ color:'#0F172A',fontWeight:500,whiteSpace:'nowrap' }}>{$$(Number(c.client_price)||0)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Totals */}
          <div style={{ paddingTop:6 }}>
            <div style={{ display:'flex',justifyContent:'space-between',fontSize:12,color:'#64748B',marginBottom:6 }}>
              <span>Your cost</span><span>{$$(totalNet)}</span>
            </div>
            <div style={{ display:'flex',justifyContent:'space-between',fontSize:14,fontWeight:600,color:'#0F172A' }}>
              <span>Client total</span><span style={{ color:G }}>{$$(totalSell)}</span>
            </div>
            <div style={{ fontSize:11,color:'#94A3B8',marginTop:4,textAlign:'right' }}>Blended margin: {blended}%</div>
          </div>

          {/* Show-pricing toggle — visually prominent so distributors don't miss it */}
          <div style={{
            background: showPricing ? `${G}10` : '#FEF3C7',
            border: `2px solid ${showPricing ? G : '#FCD34D'}`,
            borderRadius:9, padding:'12px 14px', marginTop:14,
            display:'flex', alignItems:'center', justifyContent:'space-between', gap:10,
          }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:13, fontWeight:600, color: showPricing ? '#059669' : '#92400E' }}>
                {showPricing ? '✓ Pricing visible to client' : '⚠ Pricing HIDDEN from client'}
              </div>
              <div style={{ fontSize:11, color:'#64748B', marginTop:3, lineHeight:1.4 }}>
                {showPricing
                  ? 'Client will see all prices on their proposal.'
                  : 'Client sees products but no prices.'}
                {dist && (typeof dist.show_pricing === 'boolean') && showPricing !== !!dist.show_pricing && (
                  <span style={{ color:G, marginLeft:4, fontWeight:500 }}>(overrides your default)</span>
                )}
              </div>
            </div>
            <div onClick={()=>setShowPricing(v=>!v)}
              style={{ width:46, height:26, background: showPricing ? G : '#CBD5E1',
                borderRadius:13, cursor:'pointer', position:'relative', flexShrink:0,
                transition:'background 0.15s', boxShadow:'inset 0 1px 2px rgba(0,0,0,0.1)' }}>
              <div style={{ width:20, height:20, background:'#fff', borderRadius:10,
                position:'absolute', top:3, left: showPricing ? 23 : 3,
                transition:'left 0.15s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }}/>
            </div>
          </div>
          <button onClick={handleSave} disabled={saving||isEmpty}
            style={{ width:'100%',marginTop:12,background:'#fff',border:`1px solid ${G}`,borderRadius:9,padding:10,color:G,fontSize:13,fontWeight:500,cursor:(saving||isEmpty)?'not-allowed':'pointer',fontFamily:'inherit',opacity:isEmpty?0.5:1 }}>
            {saving?'Saving...':'Save Draft'}
          </button>
          <button onClick={handlePDF} disabled={pdfLoading||isEmpty}
            style={{ width:'100%',marginTop:8,background:'#0F172A',border:'none',borderRadius:9,padding:10,color:'#fff',fontSize:13,fontWeight:500,cursor:(pdfLoading||isEmpty)?'not-allowed':'pointer',fontFamily:'inherit',opacity:isEmpty?0.5:1 }}>
            {pdfLoading?(pdfStatus||'Generating PDF...'):'Download PDF'}
          </button>
          <button onClick={handleSend} disabled={sending||!clientEmail||isEmpty}
            style={{ width:'100%',marginTop:8,background:G,border:'none',borderRadius:9,padding:11,color:'#fff',fontSize:13,fontWeight:500,
              cursor:(sending||!clientEmail||isEmpty)?'not-allowed':'pointer',fontFamily:'inherit',opacity:(!clientEmail||isEmpty)?0.6:1 }}>
            {sending?'Generating link...':'Generate Client Link'}
          </button>
          <button onClick={handleCreateInvoice} disabled={isEmpty||saving}
            style={{ width:'100%',marginTop:8,background:'#fff',border:`1px solid ${G}`,borderRadius:9,padding:10,color:G,fontSize:13,fontWeight:500,
              cursor:(isEmpty||saving)?'not-allowed':'pointer',fontFamily:'inherit',opacity:(isEmpty||saving)?0.5:1 }}>
            {saving ? 'Saving…' : 'Convert to Invoice'}
          </button>
          <button onClick={handleCreatePOs} disabled={isEmpty||saving}
            style={{ width:'100%',marginTop:8,background:'#fff',border:`1px solid ${G}`,borderRadius:9,padding:10,color:G,fontSize:13,fontWeight:500,
              cursor:(isEmpty||saving)?'not-allowed':'pointer',fontFamily:'inherit',opacity:(isEmpty||saving)?0.5:1 }}>
            {saving ? 'Saving…' : 'Create Purchase Orders'}
          </button>
          {!clientEmail&&!isEmpty&&<div style={{ fontSize:11,color:'#94A3B8',textAlign:'center',marginTop:6 }}>Enter client email to send</div>}
          {isEmpty&&<div style={{ fontSize:11,color:'#94A3B8',textAlign:'center',marginTop:6 }}>Add at least one product to send</div>}
        </div>
      </div>
      {shareModalOpen && shareToken && <ShareModal token={shareToken} onClose={()=>setShareModalOpen(false)}/>}
      {saveModalOpen && (
        <SaveModeModal
          presentationName={name}
          saving={saving}
          onUpdate={() => doSave({ asNew: false })}
          onSaveAsNew={() => doSave({ asNew: true })}
          onCancel={() => setSaveModalOpen(false)}
        />
      )}
      {showCustom && <CustomItemModal onClose={()=>setShowCustom(false)} onAdd={addProductToCart}/>}
      {showApparel && <AddApparelGarmentModal onClose={()=>setShowApparel(false)} onAdd={addProductToCart}/>}
      {showProductSearch && (
        <ProductSearchModal
          onClose={()=>setShowProductSearch(false)}
          getToken={getToken}
          addToCart={addFromSearch}
          cartItems={cartItems}
          showToast={showToast}
        />
      )}
      {showPOFlow && (
        <CreatePOFlow
          presentationId={poPresentationId || editingId}
          clientName={clientName}
          clientId={pickedClient?.id || null}
          getToken={getToken}
          onClose={()=>setShowPOFlow(false)}
          onCreated={(pos)=>{
            setShowPOFlow(false);
            const arr = Array.isArray(pos) ? pos : [];
            // Single PO → open it; multiple → the PO list (shows them all).
            navigate?.(arr.length === 1 && arr[0]?.id ? `pos/${arr[0].id}` : 'pos');
          }}
          showToast={showToast}
        />
      )}
      {showInvoiceModal && (
        <CreateInvoiceModal
          presentation={{ id: invoicePresentationId, products: [] }}
          prebuiltLineItems={invoiceLineItems}
          presentationId={invoicePresentationId}
          clientId={pickedClient?.id || null}
          clientName={clientName}
          clientEmail={clientEmail}
          onClose={()=>setShowInvoiceModal(false)}
          onCreated={(invoice)=>{
            setShowInvoiceModal(false);
            showToast('Invoice ' + (invoice?.invoice_number || '') + ' created');
            navigate?.(invoice?.id ? `invoices/${invoice.id}` : 'invoices');
          }}
          getToken={getToken}
          showToast={showToast}
        />
      )}
    </div>
  );
}
